import { warehouseWithTotals } from '../quoteWarehouseTotals';
import React from 'react';
import { createPortal } from 'react-dom';
import { isAxiosError } from 'axios';
import { LockClosedIcon, ChevronLeftIcon, ChevronRightIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
// Inter now loads globally (see src/index.tsx); this stylesheet just carries the
// drawer's left-aligned-label rule (.qled-inter .of-label).
import './quoteLaneEditorDrawer.css';
import Drawer from '../../../components/ui/Drawer';
import WarningConfirmModal from '../../../components/ui/WarningConfirmModal';
import AlertModal from '../../../components/ui/AlertModal';
import useDrawerResize from '../../../hooks/useDrawerResize';
import QuoteDetailHeader from './QuoteDetailHeader';
import NewRevisionDialog from './NewRevisionDialog';
import SendQuoteDialog from './SendQuoteDialog';
import NewCustomerModal from './NewCustomerModal';
import NewBillToModal from './NewBillToModal';
import RevisionHistoryModal from './RevisionHistoryModal';
import MigrationVerifyBadge from './MigrationVerifyBadge';
import { WarehouseLaneFields, missingWarehouseCreateFields, storageAllowsOnCreate } from './WarehouseCreateFields';
import { warehouseCreateDraft } from '../warehouseCreateDraft';
import { dupeParamsFromDraft } from '../quoteCreatePayload';
import DirectTab from '../tabs/DirectTab';
import DrayTab from '../tabs/DrayTab';
import WarehouseTab from '../tabs/WarehouseTab';
import DeliveryTab from '../tabs/DeliveryTab';
import CustomerInfoTab from '../tabs/CustomerInfoTab';
import DocumentsTab from '../tabs/DocumentsTab';
import NotesTab from '../tabs/NotesTab';
import HistoryTab from '../tabs/HistoryTab';
import Card from './Card';
import { QuoteCardScaleProvider } from './QuoteCardScaleContext';
import Row from '../../pro/components/orderform/Row';
import { Label } from '../../pro/components/orderform/Label';
import SelectInput from '../../pro/components/orderform/SelectInput';
import useEstimatedRevenue from './useEstimatedRevenue';
import useQuoteSectionCollapse from './useQuoteSectionCollapse';
import quoteService, { type RevisionCreatePayload, type ChargeCatalogRow, type StopoffFieldsPayload, type DupeCandidate, type DupeCheckParams, type FscTariff, type HandlingCatalogRow, type LaneFieldsPayload, type ProductRateFieldsPayload, type RateStandards } from '../../../services/quote.service';
import { isWarehouseQuoteError, WAREHOUSE_QUOTE_MESSAGE } from '../../../services/quoteErrors';
import type { QuoteListRow, QuoteDetailData, DirectFields, WarehouseFields, HandlingItemRow, LocationLookupRow, TariffRateRow, StopoffRow, BillToLookupRow, CustomerLookupRow, QuoteCreatePayload } from '../fixtures';
import { blankQuoteDetail } from '../fixtures';

/** Fresh transload draft for a WAREHOUSE create. In Dray (V1 "Dray/Transload",
 *  transloadquote.includehandlingindray) starts at Y — nearly every warehouse
 *  quote bundles handling into the dray, so the CSR only flips it for the rare N. */
const BLANK_TRANSLOAD: NonNullable<QuoteCreatePayload['transload']> = { movetype: '', size: '', includehandlingindray: 'Y' };
import {
  type ChargeOp, type StopoffOp,
  stageChargeAdd, stageChargeUpdate, stageChargeDelete,
  stageStopoffAdd, stageStopoffUpdate, stageStopoffDelete,
  replayChargeOps, replayStopoffOps,
  stopoffRowFromPayload, stopoffRowPatch,
} from '../quoteChildStaging';
import { FUEL_CALC_TYPES, RATE_APPLIES } from '../quoteOptions';
import { TRANSLOAD_MAP, TRANSLOAD_NUMBER_FIELDS, buildUpdatePayload, computeDirty, saveErrorMessage, todayDisplay, toISO } from '../quoteEditPatch';
import { isQuoteSecured, isPastEndDate, isAccountEditable, isMoveTypeEditable, derivedReviewDate } from '../quoteFieldRules';
import { isBrokenCustomerTariff, isDeletedCustomerTariff } from '../../../utils/fscTariff';
import ConfirmSecuredChangesModal from './ConfirmSecuredChangesModal';
import ModalHeader from '../../../components/ui/ModalHeader';
import { useModalDrag } from '../../../hooks/useModalDrag';
import FollowUpBookmark from '../../../components/FollowUpBookmark';
import { quoteFollowUp } from '../../../utils/followUpLabels';
import Tabs, { type TabDef } from '../../pro/components/Tabs';
import { openQuotePdf } from '../../../utils/quotePdf';
import PrintButton from '../../../components/printing/PrintButton';

/* ──────────────────────────────────────────────────────────────────────────
 * QuoteLaneEditorDrawer — the full per-quote editor hosted in a RIGHT-SIDE
 * drawer (Quotes — Lane Table + Editor Drawer, Task B). Clicking a lane in the
 * LaneTable (Task A) opens this; it ABSORBS the getQuote load + cache, editable
 * DirectTab, per-lane save (quoteEditPatch), Documents/Notes/History sub-tabs
 * and error/retry that previously lived in QuoteLaneRow's LanePanel, and ADDS
 * the Quote Details header (editable revision fields) + an action bar
 * (Make Revision · Duplicate · Send · View PDF).
 *
 * KEY DIFFERENCE from QuoteLaneRow: it tracks a DIRECT draft AND a REVISION
 * draft (the header's editable lifecycle fields via onRevisionChange). Save
 * patches BOTH via buildUpdatePayload(detail, draft, detail.direct, draftDirect),
 * so a status/effective/end-date/secure-date/rev-reason change is persisted
 * alongside the directquote field edits.
 *
 * Post-action behavior STAYS in the grouped context (unlike QuoteDetailPage,
 * which full-page-navigates): Make Revision reloads the drawer to the new
 * current revision; Duplicate notifies the parent (the dupe appears as a new
 * lane) + shows a notice; Send / View PDF perform the action with no
 * navigation. The parent re-fetches the group via onChanged().
 *
 * Drawer choice: reuses the shared <Drawer> but makes it RESIZABLE, matching
 * the Pro/order-detail slide-over. The editor is content-heavy, so width is
 * driven by useDrawerResize (drag handle on the panel's inner/left edge, width
 * persisted to localStorage under `quoteLaneEditorWidth`, min 400px, max
 * viewport−nav, default ~960px). The chosen width is fed into <Drawer>'s
 * `width` prop and the handle is rendered as the first child of the drawer
 * body (absolutely pinned to the panel's left edge). Rendered in `noBackdrop`
 * mode (a plain div
 * portal at z-[100], no modal <dialog>), matching the app's body-portal overlay
 * convention. The action dialogs (NewRevisionDialog / SendQuoteDialog) render
 * as SIBLINGS of the drawer so their `position: fixed` escapes the drawer
 * panel's transform and they layer ABOVE it (also z-[100]). The header's
 * Revision-History modal is NOT portaled (it's an inline `fixed` div in
 * QuoteDetailHeader), so it stays confined to the drawer panel — acceptable,
 * see the note by the action-dialog render below. Close (X / ESC / backdrop)
 * routes through the dirty-guard before invoking onClose.
 * ────────────────────────────────────────────────────────────────────────── */

type GetQuoteFn = (quoteNumber: number, revisionNumber: number) => Promise<QuoteDetailData>;
type UpdateQuoteFn = typeof quoteService.updateQuote;
type CreateRevisionFn = typeof quoteService.createRevision;
type DupeQuoteFn = typeof quoteService.dupeQuote;
type QuotePdfFn = typeof quoteService.quotePdf;

export interface QuoteLaneEditorDrawerProps {
  open: boolean;
  /** The clicked lane (has quoteNumber/revisionNumber/quoteId). null when none
   *  (always null in create mode). */
  lane: QuoteListRow | null;
  /** 'view' (default) loads an existing quote; 'create' opens a blank editable
   *  quote (unified New Quote experience). */
  mode?: 'view' | 'create';
  /** Create-mode prefill (e.g. the grouped page's "+ Add Quote" pre-scopes the
   *  new quote to a customer + bill-to). Header keys mirror the create payload. */
  initialHeader?: Partial<QuoteCreatePayload['header']>;
  onClose: () => void;
  /** Parent re-fetches the group after a save / revision / dupe. */
  onChanged?: () => void;
  // Injectable for tests; default to the real quote service.
  getQuote?: GetQuoteFn;
  updateQuote?: UpdateQuoteFn;
  createRevision?: CreateRevisionFn;
  dupeQuote?: DupeQuoteFn;
  quotePdf?: QuotePdfFn;
}

type SubTab = 'detail' | 'whse' | 'deliv' | 'docs' | 'notes' | 'hist';

/**
 * Mirrors the Pro drawer's strip, and deliberately reuses its hotkey letters for
 * the three shared tabs so Alt-chord muscle memory carries between the two
 * drawers. Docs stays Z (not D) for the same reason it does there: Chrome owns
 * Alt+D on Windows.
 *
 * There is no "Detail" tab any more. Quote is the first tab and owns everything
 * that used to sit above the strip -- the account cards and the Quote Details
 * card -- so the drawer reads like the Pro drawer, where Order Info holds all
 * the panels.
 */
const SUB_TABS: ReadonlyArray<TabDef & { id: SubTab }> = [
  { id: 'detail', label: 'Quote', hotkey: 'Q' },
  { id: 'docs', label: 'Documents', hotkey: 'Z' },
  { id: 'notes', label: 'Notes', hotkey: 'G' },
  { id: 'hist', label: 'History', hotkey: 'H' },
];

// Extra sub-tabs a WAREHOUSE quote needs. A direct quote's whole lane fits in one
// DirectTab under 'Quote'; a warehouse quote's does not — it is three substantial
// surfaces (dray rates, storage/handling, and a master-detail list of delivery lanes),
// so stacking them into one scroll would bury the lanes. Mirrors the tab split the
// full-page view uses, inserted after 'Quote' and before Documents/Notes/History.
const WHSE_SUB_TABS: ReadonlyArray<TabDef & { id: SubTab }> = [
  { id: 'whse', label: 'Warehouse', hotkey: 'W' },
  { id: 'deliv', label: 'Delivery', hotkey: 'Y' },
];

const QuoteLaneEditorDrawer: React.FC<QuoteLaneEditorDrawerProps> = ({
  open,
  lane,
  mode = 'view',
  initialHeader,
  onClose,
  onChanged,
  getQuote = quoteService.getQuote,
  updateQuote = quoteService.updateQuote,
  createRevision = quoteService.createRevision,
  dupeQuote = quoteService.dupeQuote,
  quotePdf = quoteService.quotePdf,
}) => {
  // Collapse state of the Account Details / Quote Details sections, remembered
  // per user across reloads and across opening different quotes (user settings).
  const { collapsed: sectionCollapsed, setSection } = useQuoteSectionCollapse();

  // ── Load + cache ─────────────────────────────────────────────────────────
  const [detail, setDetail] = React.useState<QuoteDetailData | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  // The revision the drawer is currently showing. Seeded from the lane; bumped
  // when "Make Revision" reloads to the new current revision (same quote #).
  const [activeRevision, setActiveRevision] = React.useState<number | null>(
    lane?.revisionNumber ?? null,
  );
  // The quote's revision set + which one is "current" (active) — drives the
  // header's Previous / Next / Future / Current navigation. `nums` is sorted
  // ascending; `current` is the latest-effective revision (the highest revision
  // whose effective date is on/before today — mirrors the backend's
  // recompute_current_revision), or null when none is eligible yet. Fetched per
  // quote on open; `revisionsNonce` forces a refetch after a New Revision.
  const [revInfo, setRevInfo] = React.useState<{ nums: number[]; current: number | null }>({ nums: [], current: null });
  const [revisionsNonce, setRevisionsNonce] = React.useState(0);

  const [subTab, setSubTab] = React.useState<SubTab>('detail');

  // ── Drafts (direct + revision) ─────────────────────────────────────────────
  const [draft, setDraft] = React.useState<QuoteDetailData | null>(null);
  const [draftDirect, setDraftDirect] = React.useState<DirectFields | null>(null);
  // Loaded WAREHOUSE quote: transloadquote fields (Dray + Warehouse tabs), edited
  // exactly like draftDirect and diffed into payload.transload on Save.
  const [draftWarehouse, setDraftWarehouse] = React.useState<WarehouseFields | null>(null);
  const [draftHandling, setDraftHandling] = React.useState<HandlingItemRow[]>([]);
  const [draftTransload, setDraftTransload] = React.useState<NonNullable<QuoteCreatePayload['transload']>>(BLANK_TRANSLOAD);
  const [draftLanes, setDraftLanes] = React.useState<NonNullable<QuoteCreatePayload['lanes']>>([]);
  // Child records (additional charges + stopoffs) are buffered locally — no
  // autosave. `draft*` drive the cards' display; `*Ops` are the pending server
  // calls replayed on Save. Temp rows use a decreasing negative id until Save.
  const [draftCharges, setDraftCharges] = React.useState<TariffRateRow[]>([]);
  const [draftStopoffs, setDraftStopoffs] = React.useState<StopoffRow[]>([]);
  // Create-only codes that QuoteDetailData doesn't carry (set on customer pick,
  // folded into the create payload). billToNumber / customerCode live on the draft.
  const [draftCodes, setDraftCodes] = React.useState<{ marketcode?: number }>({});
  // After a create-mode Save succeeds, the quote exists: `created` holds its
  // identity so the drawer flips to normal view/edit of that quote (stay in the
  // drawer) — createMode goes false and quoteNumber resolves to it.
  const [created, setCreated] = React.useState<{ quoteNumber: number; revisionNumber: number } | null>(null);
  // Dupe-check candidates surfaced before a create (V1 checkfordupes) — the modal
  // offers "Create Anyway" or opening an existing match in place.
  const [dupeCandidates, setDupeCandidates] = React.useState<DupeCandidate[] | null>(null);
  const {
    panelRef: dupePanelRef,
    panelStyle: dupePanelStyle,
    dragHandleProps: dupeDragHandleProps,
  } = useModalDrag(dupeCandidates !== null);
  // New Customer / New Bill-To modals (create mode) — opened from the typeaheads'
  // "+ Add As New …" action; `*Name` seeds the modal with the typed term.
  const [newCustomerOpen, setNewCustomerOpen] = React.useState(false);
  const [newBillToOpen, setNewBillToOpen] = React.useState(false);
  const [newCustomerName, setNewCustomerName] = React.useState('');
  const [newBillToName, setNewBillToName] = React.useState('');
  const [chargeOps, setChargeOps] = React.useState<ChargeOp[]>([]);
  const [stopoffOps, setStopoffOps] = React.useState<StopoffOp[]>([]);
  const tempIdRef = React.useRef(-1);
  const nextTempId = () => { const id = tempIdRef.current; tempIdRef.current -= 1; return id; };
  const [saving, setSaving] = React.useState(false);
  const [saveMsg, setSaveMsg] = React.useState<string | null>(null);
  const [saveErr, setSaveErr] = React.useState<string | null>(null);
  const [conflict, setConflict] = React.useState(false);
  // Secured-quote add-charge confirmation (charges can't be edited once saved).
  const [confirmAddOpen, setConfirmAddOpen] = React.useState(false);
  // v2-migration "verified" state — seeded from the loaded quote in applyDetail,
  // updated locally on toggle so it never disturbs unsaved edits.
  const [migVerified, setMigVerified] = React.useState(false);
  const [migVerifiedBy, setMigVerifiedBy] = React.useState<string | null>(null);
  const [migVerifiedDate, setMigVerifiedDate] = React.useState<string | null>(null);
  const [migBusy, setMigBusy] = React.useState(false);

  // ── Action state ───────────────────────────────────────────────────────────
  const [actionMsg, setActionMsg] = React.useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [revisionDialogOpen, setRevisionDialogOpen] = React.useState(false);
  const [dupeDialogOpen, setDupeDialogOpen] = React.useState(false);
  const [sendDialogOpen, setSendDialogOpen] = React.useState(false);
  const [creatingRevision, setCreatingRevision] = React.useState(false);
  const [duping, setDuping] = React.useState(false);
  // Shown when the user tries to close the drawer with unsaved edits.
  const [showCloseConfirm, setShowCloseConfirm] = React.useState(false);
  // New Revision guards: confirm before discarding unsaved edits; alert when the
  // current revision has no End Date (a revision needs one before it's superseded).
  const [showRevisionDirtyConfirm, setShowRevisionDirtyConfirm] = React.useState(false);
  const [showEndDateAlert, setShowEndDateAlert] = React.useState(false);

  // After a create, `created` supplies the new quote number so load/reload/
  // revisions all work against it.
  const quoteNumber = created?.quoteNumber ?? lane?.quoteNumber;
  // Create mode: a blank, editable quote (no getQuote, no revisions, Save POSTs).
  // Once created, the drawer becomes a normal view/edit of the new quote.
  const createMode = mode === 'create' && created == null;
  const warehouseCreate = createMode && draft?.quoteType === 'WAREHOUSE';
  // A LOADED warehouse quote, as opposed to one being created. Keyed off `detail` (what
  // came back from the server) rather than `draft` (what the form holds), so switching
  // the Quote Type select in create mode cannot make an existing direct quote render its
  // warehouse tabs.
  const isWarehouseQuote = !createMode && detail?.quoteType === 'WAREHOUSE';
  // Additional charges on the Quote sub-tab pin to the type's own table.
  const warehouseScoped = warehouseCreate || isWarehouseQuote;
  const chargeScope = warehouseScoped ? 'transloadquote' : 'directquote';

  // ── Resizable width ─────────────────────────────────────────────────────────
  // Drag handle on the panel's inner (left) edge; width persisted across opens
  // and sessions. Mirrors the Pro detail slide-over (useDrawerResize). Default
  // ~960px keeps the content-heavy editor uncramped on first open.
  const { width: drawerWidth, onHandleMouseDown, onHandleDoubleClick } = useDrawerResize({
    isOpen: open,
    storageKey: 'quoteLaneEditorWidth',
    minWidth: 400,
    defaultWidth: 960,
  });

  // Seed the loaded quote + BOTH drafts together so there is never a committed
  // render where `detail` exists but the drafts are still null (which would
  // momentarily render the editable inputs read-only and drop the first edit).
  const applyDetail = React.useCallback((d: QuoteDetailData) => {
    setDetail(d);
    setDraft(d);
    setDraftDirect(d.direct ?? null);
    setDraftWarehouse(d.warehouse ?? null);
    setDraftCharges(d.tariffRates ?? []);
    setDraftStopoffs(d.directStopoffs ?? []);
    setChargeOps([]);
    setStopoffOps([]);
    setMigVerified(!!d.v2MigrationVerified);
    setMigVerifiedBy(d.v2MigrationVerifiedBy ?? null);
    setMigVerifiedDate(d.v2MigrationVerifiedDate ?? null);
  }, []);

  // Toggle the v2-migration "verified" flag (badge in the drawer title). App-owned
  // write — updates only the local badge state, never the editable drafts.
  const handleToggleMigrationVerified = async (next: boolean) => {
    if (quoteNumber == null) return;
    setMigBusy(true);
    setSaveErr(null);
    try {
      const res = await quoteService.verifyMigration(quoteNumber, next);
      setMigVerified(res.v2MigrationVerified);
      setMigVerifiedBy(res.v2MigrationVerifiedBy);
      setMigVerifiedDate(res.v2MigrationVerifiedDate);
      setActionMsg(res.v2MigrationVerified ? 'Migration verified.' : 'Migration verification cleared.');
    } catch (e) {
      setSaveErr(saveErrorMessage(e) || 'Failed to update migration verification.');
    } finally {
      setMigBusy(false);
    }
  };

  const load = React.useCallback(async (rev: number) => {
    if (quoteNumber == null) {
      setLoadError('This lane has no saved quote to open.');
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const d = await getQuote(quoteNumber, rev);
      applyDetail(d);
    } catch (e: unknown) {
      setLoadError(
        isWarehouseQuoteError(e)
          ? WAREHOUSE_QUOTE_MESSAGE
          : e instanceof Error
            ? e.message
            : typeof e === 'string'
              ? e
              : 'Failed to load this quote.',
      );
    } finally {
      setLoading(false);
    }
  }, [getQuote, quoteNumber, applyDetail]);

  // Navigate the drawer to another revision of the same quote — shared by the
  // header's Previous/Future arrows and the Revision History picker.
  const goToRevision = React.useCallback((rev: number) => {
    setActiveRevision(rev);
    void load(rev);
  }, [load]);

  // Open + load (and reset all per-lane state when the lane identity changes so
  // a reused instance never shows a previous quote under a new lane).
  React.useEffect(() => {
    if (!open) return;
    if (mode !== 'create' && !lane) return;
    setSubTab('detail');
    setDetail(null);
    setSaveMsg(null);
    setSaveErr(null);
    setConflict(false);
    setActionMsg(null);
    setRevisionDialogOpen(false);
    setDupeDialogOpen(false);
    setSendDialogOpen(false);
    setShowRevisionDirtyConfirm(false);
    setShowEndDateAlert(false);
    setDraft(null);
    setDraftDirect(null);
    setDraftWarehouse(null);
    setDraftTransload(BLANK_TRANSLOAD);
    setDraftHandling([]);
    setDraftLanes([]);
    setDraftCodes({});
    setCreated(null);
    setDupeCandidates(null);
    setNewCustomerOpen(false);
    setNewBillToOpen(false);
    setRevInfo({ nums: [], current: null });
    setLoadError(null);
    // Create mode: seed a blank editable quote (no getQuote / revisions). Keyed on
    // `mode` (not the derived createMode) so setting `created` on a successful save
    // doesn't re-fire this and wipe the freshly-loaded quote.
    if (mode === 'create') {
      setActiveRevision(null);
      const shell = blankQuoteDetail();
      shell.effective = todayDisplay(); // default Effective to the creation date
      // Review Date is required to save, so seed it 30 days past Effective rather
      // than making the CSR type it on every quote. It keeps trailing Effective
      // until hand-edited (see onRevisionFieldChange).
      shell.reviewDate = derivedReviewDate(shell.effective);
      // Fuel Calculation / Rate Applies are required to save. V1 seeds
      // fuelcalctype from the bill-to default (every cms.billtos row is
      // DELIVERY DATE) and its selects carry no blank entry, so a new quote is
      // DELIVERY DATE / ON DELIVERY unless the CSR picks otherwise. A caller
      // header (grouped "+ Add Quote") or a bill-to pick still overrides.
      shell.fuelCalc = 'DELIVERY DATE';
      shell.rateApplies = 'ON DELIVERY';
      const h = initialHeader;
      if (h) {
        shell.quoteType = h.quotetype ?? 'DIRECT';
        // Prefill the account block from the caller (grouped "+ Add Quote").
        if (h.billto != null) shell.billToNumber = h.billto;
        if (h.billtoname != null) shell.billTo = h.billtoname;
        if (h.address1 != null) shell.addr1 = h.address1;
        if (h.address2 != null) shell.addr2 = h.address2;
        if (h.city != null) shell.city = h.city;
        if (h.state != null) shell.stateCode = h.state;
        if (h.zip != null) shell.zip = h.zip;
        if (h.billtofax != null) shell.billFax = h.billtofax;
        if (h.customercode != null) shell.customerCode = h.customercode;
        if (h.customername != null) shell.customer = h.customername;
        if (h.contactname != null) shell.contact = h.contactname;
        if (h.phone != null) shell.phone = h.phone;
        if (h.fax != null) shell.fax = h.fax;
        if (h.email != null) shell.email = h.email;
        if (h.csr != null) shell.csr = h.csr;
        if (h.fuelcalctype != null) shell.fuelCalc = h.fuelcalctype;
        if (h.rateapplies != null) shell.rateApplies = h.rateapplies;
        if (h.marketcode != null) setDraftCodes({ marketcode: h.marketcode });
      }
      applyDetail(shell);
      setLoading(false);
      return;
    }
    const rev = lane!.revisionNumber;
    setActiveRevision(rev);
    if (lane!.quoteNumber == null) {
      setLoadError('This lane has no saved quote to open.');
      setLoading(false);
      return;
    }
    void load(rev);
    // load is keyed on quoteNumber; the lane keys drive the reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, lane?.quoteId, lane?.quoteNumber, lane?.revisionNumber]);

  // Fetch the quote's revisions so the header can offer Previous / Next /
  // Future / Current navigation. We derive the current (active) revision the
  // same way the backend does (recompute_current_revision): the highest
  // revision whose effective date is on/before today (an empty/zero date counts
  // as eligible). Re-runs after a New Revision via `revisionsNonce`.
  React.useEffect(() => {
    if (!open || quoteNumber == null) return;
    let cancelled = false;
    quoteService.getQuoteRevisions(quoteNumber)
      .then((data) => {
        if (cancelled) return;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const eligible = (eff?: string) => {
          const t = (eff ?? '').trim();
          if (!t || t === '—' || t.startsWith('0000')) return true; // zero/empty → eligible
          const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(t);
          if (!m) return false;
          return new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2])) <= today;
        };
        const revs = data.revisions.filter((r) => r.revisionNumber != null);
        const nums = revs.map((r) => r.revisionNumber as number).sort((a, b) => a - b);
        const eligibleNums = revs.filter((r) => eligible(r.effective)).map((r) => r.revisionNumber as number);
        setRevInfo({ nums, current: eligibleNums.length ? Math.max(...eligibleNums) : null });
      })
      .catch(() => { /* non-fatal — the nav links just won't show */ });
    return () => { cancelled = true; };
  }, [open, quoteNumber, revisionsNonce]);

  // A brand-new quote (create mode) has no quote/revision number yet but is fully
  // editable; an existing quote is editable once loaded.
  const editable = !!detail && (createMode || (detail.quoteNumber != null && detail.revisionNumber != null));
  // New Revision is only offered on the LATEST revision (no later revision
  // exists → nextRevision is null); older revisions are view/Duplicate only.
  const isLatestRevision = !!detail && detail.nextRevision == null;
  // A SECURED quote is locked: every field is read-only EXCEPT End Date (V1
  // parity). `fieldsEditable` gates all field-editing callbacks; the header
  // keeps `onRevisionChange` (so End Date stays live) + a `secured` flag; New
  // Revision / Dupe / Send + Save stay available.
  const secured = editable && isQuoteSecured(detail?.rawStatus);
  const fieldsEditable = editable && !secured;
  // Customer / Bill-To (the whole account block) may be re-pointed on an unsecured
  // ORIGINAL quote (revision 1) — e.g. right after a Dupe — but stays read-only on
  // a later revision (which would re-point the shared masterquote account). Create
  // mode (no revision yet) counts as the original.
  const accountEditable = isAccountEditable(fieldsEditable, detail?.revisionNumber);
  // NEW CUSTOMER exemption: a quote SAVED with customercode=0 isn't linked to a
  // real cms.customers row, so the Customer typeahead stays open on ANY revision
  // — even secured — until the real customer is linked (the backend exempts the
  // customer identity cols from those locks while the stored code is 0). Gated
  // on the SAVED detail (not the draft) so Save+reload re-locks it; ended quotes
  // stay locked (extend the end date first), mirroring the missing-tariff rule.
  const customerRelinkable = editable && !createMode
    && detail?.customerCode === 0
    && !isPastEndDate(detail?.endDate);
  const customerEditable = accountEditable || customerRelinkable;
  // Move Type stays editable on a SECURED quote that no order has used yet
  // (SAVED lastUsed blank, not ended); once a PRO uses it, it locks with the
  // rest of the lane. Backend mirrors this in apply_save_rules.
  const moveTypeEditable = isMoveTypeEditable(fieldsEditable, secured, detail?.lastUsed, detail?.endDate);

  // Secured broken-tariff exemption: a secured (non-ended) quote whose SAVED
  // customer FSC tariff link is broken — never assigned, or pointing at a tariff
  // since deleted from fueldata — may have that tariff (re)assigned; the rest of
  // the tab stays locked (mirrors the add-only-charges deviation). Gated on the
  // SAVED row (not the draft) so the Lookup stays available until the assignment
  // is saved; after Save+reload the stored tariff re-locks it.
  const tariffDeleted = isDeletedCustomerTariff(
    detail?.direct?.fscType, detail?.direct?.fscTariff, detail?.fscTariffDeleted);
  const tariffAssignable = secured
    && !isPastEndDate(detail?.endDate)
    && isBrokenCustomerTariff(detail?.direct?.fscType, detail?.direct?.fscTariff, detail?.fscTariffDeleted);
  // "Assigned in this session" — the draft points somewhere other than the stored
  // (missing or dead) tariff. The compare against the stored number matters for
  // the deleted case, where the stored value is already non-zero.
  const tariffAssignedThisSession = tariffAssignable
    && Number(draftDirect?.fscTariff ?? 0) > 0
    && Number(draftDirect?.fscTariff ?? 0) !== Number(detail?.direct?.fscTariff ?? 0);

  const handleAssignTariff = (t: FscTariff) =>
    setDraftDirect((d) => (d ? {
      ...d,
      fscType: 'CUSTOMER',
      fscTariff: t.tariffNumber,
      fscRate: t.currentRate != null ? String(t.currentRate) : d.fscRate,
    } : d));

  const fieldDirty = !!(
    editable && detail && draft && computeDirty(detail, draft, detail.direct, draftDirect, detail.warehouse, draftWarehouse)
  );
  // Save is enabled by field edits OR any staged charge/stopoff change. In create
  // mode the transload draft is compared against its seeded blank (not a bare
  // literal) so the In Dray default alone never reads as an unsaved edit.
  const isDirty = fieldDirty || chargeOps.length > 0 || stopoffOps.length > 0
    || (createMode && (draft?.quoteType !== detail?.quoteType || JSON.stringify(draftTransload) !== JSON.stringify(BLANK_TRANSLOAD) || draftLanes.length > 0 || draftHandling.length > 0));
  // N21: a DO NOT BILL storage type must carry a reason (server guard is the
  // backstop; this is the inline gate, as on QuoteDetailPage).
  const storageReasonMissing = !!draftWarehouse
    && (draftWarehouse.storageType || '').toUpperCase() === 'DO NOT BILL'
    && !draftWarehouse.storageDoNotBillReason;

  // Header revision-field change → revision draft. Mirrors QuoteDetailPage's
  // onRevisionChange: securing the quote stamps today's secure date if none.
  const onRevisionFieldChange = (field: keyof QuoteDetailData, v: string) =>
    setDraft((d) => {
      if (!d) return d;
      const next = { ...d, [field]: v };
      if (field === 'rawStatus' && v === 'SECURED' && !String(d.statusDate ?? '').trim()) {
        next.statusDate = todayDisplay();
      }
      // Create mode only: Review Date trails Effective + 30 days for as long as it
      // is still the auto-derived value (or blank). Once the CSR types their own
      // Review Date it stops following — we never overwrite a hand-picked date.
      // Existing quotes are untouched: moving Effective on a saved revision leaves
      // the quote's Review Date alone.
      if (createMode && field === 'effective') {
        const current = String(d.reviewDate ?? '').trim();
        if (!current || current === derivedReviewDate(d.effective)) {
          next.reviewDate = derivedReviewDate(v);
        }
      }
      return next;
    });

  const onDirectChange = (field: keyof DirectFields, v: string) =>
    setDraftDirect((d) => (d ? { ...d, [field]: v } : d));
  const onWarehouseChange = (field: keyof WarehouseFields, v: string) => {
    if (warehouseCreate) {
      const column = TRANSLOAD_MAP[field];
      if (!column) return;
      setDraftTransload((d) => ({ ...d, [column]: TRANSLOAD_NUMBER_FIELDS.has(field) ? Number(v) || 0 : v,
        ...(field === 'storageType' && !storageAllowsOnCreate(v, 'storagercvmonthfree') ? { storagercvmonthfree: 'N' } : {}),
      }));
    } else setDraftWarehouse((d) => (d ? { ...d, [field]: v } : d));
  };
  const warehouseData = warehouseCreate ? warehouseCreateDraft(draftTransload)
    : fieldsEditable && draftWarehouse ? draftWarehouse : detail?.warehouse;

  const onDirectPickLocation = (row: LocationLookupRow) =>
    setDraftDirect((d) => (d ? {
      ...d,
      deliverToName: row.lname,
      deliverToAddr1: row.address1,
      deliverToAddr2: row.address2,
      deliverToCity: row.city,
      deliverToState: row.state,
      deliverToZip: row.zip,
      deliverToCode: row.locationcode,
    } : d));

  // ── Create-mode account lookups (Bill To / Customer typeaheads) ─────────────
  // Typing renames + unlinks (code → 0); picking links the real record and
  // re-denormalizes its fields onto the quote (mirrors the create wizard).
  const handleTypeBillTo = (name: string) =>
    setDraft((d) => (d ? { ...d, billTo: name, billToNumber: 0 } : d));
  const handlePickBillTo = (row: BillToLookupRow) =>
    setDraft((d) => (d ? {
      ...d,
      billTo: row.billtoname, billToNumber: row.billto,
      addr1: row.address1, addr2: row.address2, city: row.city,
      stateCode: row.state, zip: row.zip, billFax: row.billtofax,
      sageTag: row.mas90code, credTerms: row.creditTerms, credHold: row.creditHold,
      arBalance: row.arBalance, salesCsr: row.salescsr,
      keyAccount: d.keyAccount || row.keyAccount,
      // Seed fuel calc from the bill-to's default when the user hasn't set one.
      fuelCalc: d.fuelCalc || row.defaultFuelCalc,
    } : d));
  const handleTypeCustomer = (name: string) =>
    setDraft((d) => (d ? { ...d, customer: name, customerCode: 0 } : d));
  const handlePickCustomer = (row: CustomerLookupRow) => {
    setDraft((d) => (d ? {
      ...d,
      customer: row.custname, customerCode: row.customercode,
      market: row.market, keyAccount: row.keyAccount,
      // Quote Details "Sales Rep" reflects the picked customer's rep (name when
      // resolvable, else the username).
      salesRep: row.salesRepName || row.salesrep,
    } : d));
    setDraftCodes((c) => ({ ...c, marketcode: row.marketcode ?? undefined }));
  };
  // "+ Add As New …" — open the modal seeded with the typed term.
  const handleAddNewBillTo = (term: string) => { setNewBillToName(term); setNewBillToOpen(true); };
  const handleAddNewCustomer = (term: string) => { setNewCustomerName(term); setNewCustomerOpen(true); };

  // Est. Revenue = (dray w/charges + FSC adjustment) × volume (V1
  // updateestimatedrevenue). Lives here — not in DirectTab — so it keeps
  // recomputing while the Direct sub-tab is unmounted (volume edits stay live).
  useEstimatedRevenue({
    dray: draftDirect?.drayWithCharges ?? '',
    fsctype: draftDirect?.fscType ?? '',
    fsctariff: draftDirect?.fscTariff != null ? String(draftDirect.fscTariff) : '',
    deliveryzone: draftDirect?.deliveryZone ?? '',
    fscrate: draftDirect?.fscRate ?? '',
    miles: draftDirect?.deliverToMiles ?? '',
    volume: draft?.volume ?? '',
    estRev: draft?.estRev ?? '',
    enabled: fieldsEditable && !!draftDirect && !warehouseCreate,
    onEstRev: (v) => onRevisionFieldChange('estRev', v),
  });

  const revert = () => {
    setDraft(detail);
    setDraftDirect(detail?.direct ?? null);
    setDraftWarehouse(detail?.warehouse ?? null);
    setDraftCharges(detail?.tariffRates ?? []);
    setDraftStopoffs(detail?.directStopoffs ?? []);
    setChargeOps([]);
    setStopoffOps([]);
    setSaveErr(null);
    setSaveMsg(null);
    setConflict(false);
  };

  const reload = React.useCallback(async (rev: number) => {
    if (quoteNumber == null) return;
    const fresh = await getQuote(quoteNumber, rev);
    applyDetail(fresh); // re-seeds drafts in lock-step with detail
  }, [getQuote, quoteNumber, applyDetail]);

  // Additional charges use the selected type’s catalog and remain staged
  // until Create/Save. Rows from the other draft type are retained separately.
  const [chargeCatalog, setChargeCatalog] = React.useState<ChargeCatalogRow[]>([]);
  React.useEffect(() => {
    if (!editable) return;
    let cancelled = false;
    quoteService.chargeCatalog(warehouseScoped ? 'transload' : 'direct')
      .then((catalogRows) => { if (!cancelled) setChargeCatalog(catalogRows); })
      .catch(() => { /* add-row select just renders empty */ });
    return () => { cancelled = true; };
  }, [editable, warehouseScoped]);

  // ── WAREHOUSE-only lookups (loaded warehouse quote, editable) ──────────────
  // Delivery-lane charges use the 'delivery' charge scope (V1 additionalcharges
  // with delivery='Y'); handling items pick from handlingitems; rate standards
  // feed the below-minimum dray / delivery / transload warnings.
  const [deliveryChargeCatalog, setDeliveryChargeCatalog] = React.useState<ChargeCatalogRow[]>([]);
  const [handlingCatalog, setHandlingCatalog] = React.useState<HandlingCatalogRow[]>([]);
  const [rateStandards, setRateStandards] = React.useState<RateStandards | null>(null);
  React.useEffect(() => {
    // `editable` (not fieldsEditable): a secured quote still ADDS lane charges.
    if (!(editable && warehouseScoped)) return;
    let cancelled = false;
    quoteService.chargeCatalog('delivery')
      .then((rows) => { if (!cancelled) setDeliveryChargeCatalog(rows); })
      .catch(() => { /* add-row select just renders empty */ });
    quoteService.handlingCatalog()
      .then((rows) => { if (!cancelled) setHandlingCatalog(rows); })
      .catch(() => { /* add-row select just renders empty */ });
    quoteService.rateStandards()
      .then((std) => { if (!cancelled) setRateStandards(std); })
      .catch(() => { /* warnings simply don't render */ });
    return () => { cancelled = true; };
  }, [editable, fieldsEditable, warehouseScoped]);

  // WAREHOUSE child records (handling items, product rates, delivery lanes and
  // their stopoffs / charges) round-trip immediately and reload the quote —
  // the same pattern QuoteDetailPage uses — rather than being staged like the
  // directquote charges/stopoffs. Field edits must be saved or reverted first
  // so the reload never discards them.
  const childOp = async (fn: () => Promise<unknown>) => {
    if (!detail || detail.quoteNumber == null || detail.revisionNumber == null) return false;
    if (isDirty) {
      setSaveErr('Save or revert your field changes before editing lanes, handling or charges.');
      return false;
    }
    setSaveErr(null);
    try {
      await fn();
      await reload(detail.revisionNumber);
      onChanged?.();
      return true;
    } catch (e: unknown) {
      setSaveErr(saveErrorMessage(e));
      return false;
    }
  };
  const handlingItems = warehouseCreate ? draftHandling : detail?.handlingItems ?? [];
  const stageHandling = (p: NonNullable<QuoteCreatePayload['handling']>[number]) => {
    const item = handlingCatalog.find((r) => r.recno === p.handlingitemrecno);
    setDraftHandling((rows) => [...rows, {
      id: nextTempId(), handlingItemRecno: p.handlingitemrecno,
      description: item?.description ?? '', handlingType: p.handlingtype ?? 'handlingin',
      rateType: p.handlingratetype ?? item?.rateType ?? '', rate: String(p.handlingrate ?? 0),
      unitType: p.handlingunittype ?? item?.unitType ?? '',
    }]);
  };
  const changeStagedHandling = (id: number, patch: { handlingratetype?: string; handlingrate?: number; handlingunittype?: string }) =>
    setDraftHandling((rows) => rows.map((r) => r.id === id ? {
      ...r, rateType: patch.handlingratetype ?? r.rateType,
      rate: patch.handlingrate == null ? r.rate : String(patch.handlingrate),
      unitType: patch.handlingunittype ?? r.unitType,
    } : r));
  const qn = () => detail!.quoteNumber!;
  const rv = () => detail!.revisionNumber!;
  const handleAddHandling = (p: { handlingitemrecno: number; handlingtype: 'handlingin' | 'handlingout'; handlingratetype: string; handlingrate: number; handlingunittype?: string }) =>
    childOp(() => quoteService.addHandling(qn(), rv(), p));
  const handleChangeHandling = (id: number, patch: { handlingratetype?: string; handlingrate?: number; handlingunittype?: string }) =>
    childOp(() => quoteService.updateHandling(qn(), rv(), id, patch));
  const handleDeleteHandling = (id: number) => {
    if (!window.confirm('Remove this handling item?')) return;
    void childOp(() => quoteService.deleteHandling(qn(), rv(), id));
  };
  const handleAddProductRate = (p: ProductRateFieldsPayload) =>
    childOp(() => quoteService.addProductRate(qn(), rv(), p));
  const handleUpdateProductRate = (id: number, p: ProductRateFieldsPayload) =>
    childOp(() => quoteService.updateProductRate(qn(), rv(), id, p));
  const handleDeleteProductRate = (id: number) => {
    if (!window.confirm('Remove this product rate?')) return;
    void childOp(() => quoteService.deleteProductRate(qn(), rv(), id));
  };
  const handleAddLane = (payload: LaneFieldsPayload) =>
    childOp(() => quoteService.addLane(qn(), rv(), payload));
  const handleSaveLane = (laneId: number, payload: LaneFieldsPayload) =>
    childOp(() => quoteService.updateLane(qn(), rv(), laneId, payload));
  const handleDeleteLane = (laneId: number) => {
    if (!window.confirm('Delete this delivery lane?')) return;
    void childOp(() => quoteService.deleteLane(qn(), rv(), laneId));
  };
  const handleAddLaneStopoff = (laneId: number, payload: StopoffFieldsPayload) =>
    childOp(() => quoteService.addStopoff(qn(), rv(), { ...payload, laneRecno: laneId }));
  const handleUpdateLaneStopoff = (stopId: number, payload: StopoffFieldsPayload) =>
    childOp(() => quoteService.updateStopoff(qn(), rv(), stopId, payload));
  const handleDeleteLaneStopoff = (stopId: number) =>
    childOp(() => quoteService.deleteStopoff(qn(), rv(), stopId));
  // A lane charge pins tablename='deliveryquote' + the lane recno (tablerecno).
  // Lane charges persist immediately (no staged Save), so on a SECURED quote
  // the permanence warning the Direct tab shows at Save time is asked up front.
  const handleAddLaneCharge = (laneRecno: number, p: { additionalchargesrecno: number; addlchargeratetype: string; addlchargerate: number }) => {
    if (secured && !window.confirm('This quote is secured. A charge added now is permanent — it cannot be edited or removed afterward. Add it?')) return;
    return childOp(() => quoteService.addCharge(qn(), rv(), { ...p, tablename: 'deliveryquote', tablerecno: laneRecno }));
  };
  const handleChangeLaneCharge = (id: number, patch: { addlchargeratetype?: string; addlchargerate?: number }) =>
    childOp(() => quoteService.updateCharge(qn(), rv(), id, patch));
  const handleDeleteLaneCharge = (id: number) =>
    childOp(() => quoteService.deleteCharge(qn(), rv(), id));

  // CSR assignment roster for the always-editable CSR typeahead (V1 buildcsr_data).
  const [csrOptions, setCsrOptions] = React.useState<{ value: string; label: string }[]>([]);
  React.useEffect(() => {
    if (!editable) return;
    let cancelled = false;
    quoteService.getCsrOptions()
      .then((rows) => { if (!cancelled) setCsrOptions(rows); })
      .catch(() => { /* CSR typeahead just renders its current value with no options */ });
    return () => { cancelled = true; };
  }, [editable]);

  // Rate Zone options for the DirectTab dropdown. V1 sources every rate-zone
  // select from cms.ratezones (defs_quote.pl); value = ratezone CODE (stored),
  // label = displayname (shown). Without this the tab falls back to a stale
  // hardcoded subset that omits live zones like DAL-KCS ("DALLAS - CP/KCS").
  const [rateZoneOptions, setRateZoneOptions] = React.useState<{ value: string; label: string }[]>([]);
  React.useEffect(() => {
    if (!editable) return;
    let cancelled = false;
    quoteService.rateZones()
      .then((rows) => {
        if (!cancelled) setRateZoneOptions(rows.map((r) => ({ value: r.rateZone, label: r.displayName || r.rateZone })));
      })
      .catch(() => { /* DirectTab falls back to the static RATE_ZONES list */ });
    return () => { cancelled = true; };
  }, [editable]);

  const handleAddCharge = (p: { additionalchargesrecno: number; addlchargeratetype: string; addlchargerate: number; freetime?: number; freetimeunit?: string; additionaltype?: string; additionaldays?: number }) => {
    const tempId = nextTempId();
    const desc = chargeCatalog.find((c) => c.recno === p.additionalchargesrecno)?.description ?? '';
    setDraftCharges((rows) => [...rows, {
      id: tempId, description: desc, rateType: p.addlchargeratetype ?? '',
      rate: String(p.addlchargerate ?? ''), scope: chargeScope, pdfMessage: '',
      additionalChargesRecno: p.additionalchargesrecno,
      freeTime: p.freetime != null ? String(p.freetime) : '', freeTimeUnit: p.freetimeunit ?? '',
      additionalType: p.additionaltype ?? '', additionalDays: p.additionaldays ?? 0,
    }]);
    setChargeOps((ops) => stageChargeAdd(ops, tempId, { ...p, tablename: chargeScope }));
  };
  const handleChangeCharge = (id: number, patch: { addlchargeratetype?: string; addlchargerate?: number; freetime?: number; freetimeunit?: string; additionaltype?: string; additionaldays?: number }) => {
    // On a secured quote only THIS-session additions (negative temp id) may be
    // edited; charges already saved to the quote are locked.
    if (secured && id >= 0) return;
    setDraftCharges((rows) => rows.map((r) => r.id === id ? {
      ...r,
      ...(patch.addlchargeratetype !== undefined ? { rateType: patch.addlchargeratetype } : {}),
      ...(patch.addlchargerate !== undefined ? { rate: String(patch.addlchargerate) } : {}),
      ...(patch.freetime !== undefined ? { freeTime: String(patch.freetime) } : {}),
      ...(patch.freetimeunit !== undefined ? { freeTimeUnit: patch.freetimeunit } : {}),
      ...(patch.additionaltype !== undefined ? { additionalType: patch.additionaltype } : {}),
      ...(patch.additionaldays !== undefined ? { additionalDays: patch.additionaldays } : {}),
    } : r));
    setChargeOps((ops) => stageChargeUpdate(ops, id, patch));
  };
  const handleDeleteCharge = (id: number) => {
    // Secured: a saved charge can't be removed; only this-session drafts.
    if (secured && id >= 0) return;
    if (!window.confirm('Remove this charge from the quote?')) return;
    setDraftCharges((rows) => rows.filter((r) => r.id !== id));
    setChargeOps((ops) => stageChargeDelete(ops, id));
  };

  const handleAddStopoff = (payload: StopoffFieldsPayload) => {
    const tempId = nextTempId();
    setDraftStopoffs((rows) => [...rows, stopoffRowFromPayload(payload, tempId, rows.length)]);
    setStopoffOps((ops) => stageStopoffAdd(ops, tempId, payload));
  };
  const handleUpdateStopoff = (stopId: number, payload: StopoffFieldsPayload) => {
    setDraftStopoffs((rows) => rows.map((r) => r.stopId === stopId ? { ...r, ...stopoffRowPatch(payload) } : r));
    setStopoffOps((ops) => stageStopoffUpdate(ops, stopId, payload));
  };
  const handleDeleteStopoff = (stopId: number) => {
    if (!window.confirm('Remove this stopoff?')) return;
    setDraftStopoffs((rows) => rows.filter((r) => r.stopId !== stopId));
    setStopoffOps((ops) => stageStopoffDelete(ops, stopId));
  };

  // Adding a charge to a SECURED quote is permanent (it can't be edited or
  // removed afterward), so gate the Save behind an explicit confirmation that
  // lists the additions. Non-secured saves (and secured saves with no new
  // charge) go straight through.
  const handleSave = async (): Promise<boolean> => {
    // A secured quote's two permanent deviations (a newly-added charge or a
    // freshly-assigned missing tariff) are confirmed before Save persists them.
    if (secured && (chargeOps.some((op) => op.kind === 'add') || tariffAssignedThisSession)) {
      setConfirmAddOpen(true);
      return false;
    }
    return performSave();
  };

  const handleConfirmAddCharges = async () => {
    await performSave();       // surfaces success / error / conflict in the drawer chrome
    setConfirmAddOpen(false);  // close regardless so any error banner is visible
  };

  // Returns true when the save succeeded (or there was nothing to save), false
  // on error/conflict. Persists field edits AND replays staged charge/stopoff
  // ops in one Save — quotes never autosave.
  const performSave = async (): Promise<boolean> => {
    if (!editable || !isDirty || !detail || !draft) return true;
    if (saving) return false;
    if (detail.quoteNumber == null || detail.revisionNumber == null) return false;
    // Review Date is required to save (applies to charge-only saves too).
    if (!(draft.reviewDate ?? '').trim()) { setSaveErr('Review Date is required.'); return false; }
    // Delivery Type likewise — the server 422s without it, so catch it here for
    // a readable message. Only for a DIRECT quote (deliveryzone is a directquote
    // column); guarded on draftDirect so a charge-only save still applies it.
    if (draftDirect && !(draftDirect.deliveryZone ?? '').trim()) {
      setSaveErr('Delivery Type is required.'); return false;
    }
    // Fuel Type likewise — a blank fsctype means the invoice never charges FSC.
    if (draftDirect && !(draftDirect.fscType ?? '').trim()) {
      setSaveErr('Fuel Type is required.'); return false;
    }
    // Fuel Calculation + Rate Applies are masterquote fields (no draftDirect
    // guard) and stay editable on a secured/ended quote, so a legacy blank can
    // always be supplied right here rather than blocking the save forever.
    if (!(draft.fuelCalc ?? '').trim()) { setSaveErr('Fuel Calculation is required.'); return false; }
    if (!(draft.rateApplies ?? '').trim()) { setSaveErr('Rate Applies is required.'); return false; }
    if (storageReasonMissing) { setSaveErr('A DO NOT BILL storage type needs a reason.'); return false; }
    const qn = detail.quoteNumber;
    const rev = detail.revisionNumber;
    setSaving(true);
    setSaveErr(null);
    setSaveMsg(null);
    setConflict(false);
    try {
      // Patch the revision draft plus whichever type draft applies (direct or
      // transload) in one payload — only when a field actually changed (a
      // charges-only save sends no field PATCH).
      if (fieldDirty) {
        const { payload } = buildUpdatePayload(detail, draft, detail.direct, draftDirect, detail.warehouse, draftWarehouse);
        // A customer change carries the new customer's market code (tracked in
        // draftCodes, outside the QuoteDetailData draft that buildUpdatePayload
        // diffs). Fold it into the header patch so marketcode follows the customer.
        const customerChanged = (draft.customerCode ?? 0) !== (detail.customerCode ?? 0);
        if (customerChanged && draftCodes.marketcode != null) {
          payload.header = { ...(payload.header ?? {}), marketcode: draftCodes.marketcode };
        }
        const result = await updateQuote(qn, rev, payload);
        // Departed-staff notice for a non-LC saver (an LC saver is 422'd instead):
        // the save landed, so surface it in the amber banner, not the red one.
        if (result?.warnings?.length) setActionMsg(result.warnings.join(' '));
      }
      await replayChargeOps(quoteService, qn, rev, chargeOps);
      await replayStopoffOps(quoteService, qn, rev, stopoffOps);
      await reload(rev); // re-seeds drafts + clears staged ops in lock-step
      setSaveMsg('Saved.');
      onChanged?.();
      return true;
    } catch (e: unknown) {
      if (isAxiosError(e) && e.response?.status === 409) {
        setConflict(true);
      } else {
        setSaveErr(saveErrorMessage(e));
      }
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleConflictReload = async () => {
    setConflict(false);
    if (!detail || detail.revisionNumber == null) return;
    try {
      await reload(detail.revisionNumber);
    } catch {
      setSaveErr('Failed to reload the quote. Please refresh the page.');
    }
  };

  // ── Create (POST /quotes) ────────────────────────────────────────────────────
  // Required fields mirror the create wizard: Bill To (a real recno OR a typed
  // new-bill-to name), Customer (real code + name), Move Type, Size.
  const createValidationMissing = (): string[] => {
    const missing: string[] = [];
    if (!((draft?.billToNumber ?? 0) > 0 || (draft?.billTo ?? '').trim())) missing.push('Bill To');
    if (!((draft?.customerCode ?? 0) > 0 && (draft?.customer ?? '').trim())) missing.push('Customer');
    if (!(warehouseCreate ? draftTransload.movetype : draftDirect?.moveType ?? '').trim()) missing.push('Move Type');
    if (!(warehouseCreate ? draftTransload.size : draftDirect?.size ?? '').trim()) missing.push('Size');
    if (!warehouseCreate && !(draftDirect?.deliveryZone ?? '').trim()) missing.push('Delivery Type');
    if (!(warehouseCreate ? draftTransload.fueltype ?? '' : draftDirect?.fscType ?? '').trim()) missing.push('Fuel Type');
    // V1 required transloadquote fields (defs_quote.pl 'Y' flags).
    if (warehouseCreate) missing.push(...missingWarehouseCreateFields(draftTransload));
    if (!(draft?.reviewDate ?? '').trim()) missing.push('Review Date');
    if (!(draft?.fuelCalc ?? '').trim()) missing.push('Fuel Calculation');
    if (!(draft?.rateApplies ?? '').trim()) missing.push('Rate Applies');
    return missing;
  };

  const createDupeParams = (): DupeCheckParams => dupeParamsFromDraft(buildCreatePayload());

  // Map the QuoteDetailData draft (+ direct/charges/codes) to the create payload.
  const buildCreatePayload = (): QuoteCreatePayload => {
    const d = draft!;
    const dir = draftDirect!;
    const s = (v?: string | null) => { const t = (v ?? '').trim(); return t || undefined; };
    const n = (v?: string | number | null) => {
      if (v == null || v === '') return undefined;
      const num = Number(v); return Number.isFinite(num) ? num : undefined;
    };
    return {
      header: {
        billto: d.billToNumber ?? 0,
        billtoname: s(d.billTo),
        address1: s(d.addr1), address2: s(d.addr2),
        city: s(d.city), state: s(d.stateCode), zip: s(d.zip), billtofax: s(d.billFax),
        customercode: d.customerCode ?? undefined,
        customername: (d.customer ?? '').trim(),
        marketcode: draftCodes.marketcode,
        quotetype: warehouseCreate ? 'WAREHOUSE' : 'DIRECT',
        csr: s(d.csr),
        contactname: s(d.contact), phone: s(d.phone), fax: s(d.fax), email: s(d.email),
        fuelcalctype: s(d.fuelCalc),
        rateapplies: s(d.rateApplies),
        // masterquote.reviewdate — required on create (backend 422s if blank).
        // Sent as ISO to match the edit PATCH's toISO() mapping for reviewDate.
        reviewdate: toISO(d.reviewDate) || undefined,
        salesforceopportunityid: s(d.sfOpp),
      },
      revision: {
        volume: n(d.volume),
        estimatedrevenue: n(d.estRev),
        estimatedmargin: n(d.estMargin),
        effectivedate: toISO(d.effective) || null,
        enddate: toISO(d.endDate) || null,
      },
      ...(warehouseCreate ? { transload: draftTransload, lanes: draftLanes,
        handling: draftHandling.map((r) => ({ handlingitemrecno: r.handlingItemRecno,
          handlingtype: r.handlingType as 'handlingin' | 'handlingout', handlingratetype: r.rateType,
          handlingrate: Number(r.rate) || 0, handlingunittype: r.unitType })),
      } : { direct: {
        movetype: (dir.moveType ?? '').trim(),
        ratezone: s(dir.rateZone), deliveryzone: s(dir.deliveryZone),
        delivertoname: s(dir.deliverToName),
        delivertoaddr1: s(dir.deliverToAddr1), delivertoaddr2: s(dir.deliverToAddr2),
        delivertocity: s(dir.deliverToCity), delivertostate: s(dir.deliverToState), delivertozip: s(dir.deliverToZip),
        delivertomiles: n(dir.deliverToMiles),
        delivertocode: dir.deliverToCode || undefined,
        size: (dir.size ?? '').trim(),
        equiptype: s(dir.equipType), commodity: s(dir.commodity),
        lbs: n(dir.lbs), kgs: n(dir.kgs),
        legalweight: dir.legalWeight, overweight: dir.overweight, oog: dir.oog,
        triaxle: dir.triaxle, swing: dir.swing, hazmat: dir.hazmat,
        chassistype: s(dir.chassisType),
        drayrate: n(dir.drayRate), draywithcharges: n(dir.drayWithCharges), draytype: s(dir.drayType),
        fscrate: n(dir.fscRate), fsctype: s(dir.fscType), fsctariff: dir.fscTariff ?? undefined,
        comments: s(dir.comments),
        hvc: dir.ess,               // frontend "ESS" ↔ directquote.hvc
        cargovalue: dir.cargoValue ?? undefined,
        waitingtimefree: n(dir.waitingTimeFree),
        yardstoragefree: dir.yardStorageFree ?? undefined,
        chassisfeetype: s(dir.chassisFeeType),
        chassisfeedays: dir.chassisFeeDays ?? undefined,
        lightweightchassisfeetype: s(dir.lightweightChassisFeeType),
        lightweightchassisfeedays: dir.lightweightChassisFeeDays ?? undefined,
      } }),
      tariffRates: draftCharges
        .filter((r) => r.scope === chargeScope && (r.additionalChargesRecno ?? 0) > 0)
        .map((r) => ({
          additionalchargesrecno: r.additionalChargesRecno!,
          addlchargeratetype: s(r.rateType),
          addlchargerate: n(r.rate) ?? 0,
          freetime: n(r.freeTime) ?? 0,
          freetimeunit: s(r.freeTimeUnit),
          additionaltype: s(r.additionalType), additionaldays: r.additionalDays ?? 0,
        })),
    };
  };

  // Load an existing quote INTO this drawer (stay-in-drawer) — used after a
  // successful create and by the dupe modal's "open an existing match" action.
  const enterQuote = React.useCallback(async (qn: number, rev: number) => {
    setCreated({ quoteNumber: qn, revisionNumber: rev });
    setActiveRevision(rev);
    setDupeCandidates(null);
    setSubTab('detail');
    setLoadError(null);
    try {
      const fresh = await getQuote(qn, rev);
      applyDetail(fresh);
    } catch (e) {
      setLoadError(
        isWarehouseQuoteError(e)
          ? WAREHOUSE_QUOTE_MESSAGE
          : e instanceof Error
            ? e.message
            : 'Failed to load the quote.',
      );
    }
  }, [getQuote, applyDetail]);

  const handleCreate = async (bypass = false) => {
    if (!createMode || saving || !draft || !draftDirect) return;
    const missing = createValidationMissing();
    if (missing.length) { setSaveErr(`Please fill in: ${missing.join(', ')}.`); return; }
    setSaving(true);
    setSaveErr(null);
    setSaveMsg(null);
    try {
      // V1 checkfordupes — deferred for a new bill-to (billto=0).
      if (!bypass && (draft.billToNumber ?? 0) > 0) {
        const dupes = await quoteService.dupeCheck(createDupeParams());
        if (dupes.length > 0) { setDupeCandidates(dupes); setSaving(false); return; }
      }
      const result = await quoteService.createQuote(buildCreatePayload());
      await enterQuote(result.quoteNumber, result.revisionNumber);
      setSaveMsg(`Created quote ${result.quoteId}.`);
      // Same departed-staff notice as performSave, for a non-LC creator.
      if (result.warnings?.length) setActionMsg(result.warnings.join(' '));
      onChanged?.();
    } catch (e) {
      setSaveErr(saveErrorMessage(e) || 'Failed to create quote.');
    } finally {
      setSaving(false);
    }
  };

  // ── Actions ─────────────────────────────────────────────────────────────────
  const guardDirtyAction = (): boolean => {
    if (isDirty) {
      setActionMsg('Save or revert your changes first.');
      return false;
    }
    setActionMsg(null);
    return true;
  };

  // Open the New Revision dialog, but first require an End Date on the current
  // (saved) revision — a revision should carry one before a new revision
  // supersedes it. (The new revision copies from the SAVED current revision.)
  const proceedMakeRevision = () => {
    if (!String(detail?.endDate ?? '').trim()) {
      setShowEndDateAlert(true);
      return;
    }
    setActionMsg(null);
    setRevisionDialogOpen(true);
  };

  const handleMakeRevision = () => {
    if (!editable) return;
    // Unsaved changes → confirm (Cancel to save / Discard to continue) rather
    // than silently blocking with only the bottom-left "Unsaved changes" hint.
    if (isDirty) { setShowRevisionDirtyConfirm(true); return; }
    proceedMakeRevision();
  };

  const submitNewRevision = async (payload: RevisionCreatePayload) => {
    if (!detail || detail.quoteNumber == null || creatingRevision) return;
    setCreatingRevision(true);
    try {
      const result = await createRevision(detail.quoteNumber, payload);
      setRevisionDialogOpen(false);
      // STAY in context: reload the drawer to the NEW current revision.
      setActiveRevision(result.revisionNumber);
      setRevisionsNonce((n) => n + 1); // refetch the revision set (+ recompute current)
      await load(result.revisionNumber);
      onChanged?.();
      setActionMsg(`Created revision ${result.quoteId}.`);
    } catch (e: unknown) {
      setActionMsg(saveErrorMessage(e));
      setRevisionDialogOpen(false);
    } finally {
      setCreatingRevision(false);
    }
  };

  const handleDuplicate = () => {
    if (!editable) return;
    if (!guardDirtyAction()) return;
    setDupeDialogOpen(true);
  };

  const submitDupe = async (payload: RevisionCreatePayload) => {
    if (!detail || detail.quoteNumber == null || detail.revisionNumber == null || duping) return;
    setDuping(true);
    try {
      const result = await dupeQuote(detail.quoteNumber, detail.revisionNumber, payload);
      setDupeDialogOpen(false);
      // Swap the drawer to the newly-duped quote in place (stay on the Quotes
      // list) — same stay-in-drawer mechanism the create flow uses. onChanged
      // still refetches the list so the dupe also appears there behind the drawer.
      await enterQuote(result.quoteNumber, result.revisionNumber);
      onChanged?.();
      setActionMsg(`Duplicated as quote ${result.quoteId}.`);
    } catch (e: unknown) {
      setActionMsg(saveErrorMessage(e));
      setDupeDialogOpen(false);
    } finally {
      setDuping(false);
    }
  };

  const handleSend = () => {
    if (!editable) return;
    setActionMsg(null);
    setSendDialogOpen(true);
  };

  const handleViewPdf = async () => {
    if (!detail || detail.quoteNumber == null || detail.revisionNumber == null) return;
    const opened = await openQuotePdf(detail.quoteNumber, detail.revisionNumber, quotePdf);
    if (!opened) setActionMsg('Failed to render the quote PDF. Please try again.');
  };

  // ── Close guard ─────────────────────────────────────────────────────────────
  // Vetoes the drawer's close when there are unsaved edits and shows an in-app
  // confirm modal instead of a non-blocking window.confirm. Returning false from
  // Drawer.onBeforeClose keeps the drawer open until the user chooses.
  const requestClose = (): boolean => {
    if (isDirty) { setShowCloseConfirm(true); return false; }
    return true;
  };

  const headerData = editable && draft ? draft : detail;

  // Secured "add-only" charges: a secured quote is otherwise locked, but the
  // user may ADD new charges (confirmed before save). Not offered once the end
  // date is in the past — an ended quote is fully locked (front and back).
  const endedPast = isPastEndDate(headerData?.endDate);
  const chargesAddOnly = secured && !endedPast;
  // The charges card is interactive when normally editable OR in add-only mode.
  const chargesInteractive = fieldsEditable || chargesAddOnly;

  // Where the viewed revision sits in the quote's revision set. `viewedRev` is
  // `detail.revisionNumber` (the loaded data) — NOT the clicked lane — so the
  // header tracks revision navigation. `currentRev` is the active revision.
  const { nums: revNums, current: currentRev } = revInfo;
  const viewedRev = detail?.revisionNumber ?? null;
  const isCurrentRevision = viewedRev != null && currentRev != null && viewedRev === currentRev;
  const prevRev = viewedRev == null ? null : ([...revNums].reverse().find((n) => n < viewedRev) ?? null);
  const nextRev = viewedRev == null ? null : (revNums.find((n) => n > viewedRev) ?? null);
  // Forward link label: the active revision → "Current"; a revision dated
  // beyond the active → "Future"; an intermediate newer-but-still-≤-current
  // revision → "Next".
  const forwardLabel = nextRev == null ? null
    : (currentRev != null && nextRev === currentRev) ? 'Current'
    : (currentRev == null || nextRev > currentRev) ? 'Future'
    : 'Next';
  // Backward link label: stepping back onto the active revision reads as
  // "Current", otherwise "Previous".
  const backwardLabel = prevRev == null ? null
    : (currentRev != null && prevRev === currentRev) ? 'Current'
    : 'Previous';

  // Drawer title: "[DIRECT QUOTE] [Locked] [‹ Previous] 112219-3 (Current
  // Revision) [Future / Next / Current ›]". The quote-type pill leads, then the
  // Locked badge (when secured), then revision navigation around the bare quote
  // number (no "Quote" word / customer — those live in the Account Details
  // card). The number reflects the LOADED revision, so navigating the history
  // picker or the arrows keeps the header in sync. Forward/back link wording
  // depends on where the target sits relative to the active revision.
  const revNavBase = 'inline-flex items-center gap-0.5 text-[0.75rem] font-semibold hover:underline whitespace-nowrap';
  const revNavBlue = `${revNavBase} text-blue-600 dark:text-blue-400`;
  // "Current" links are green and "Future" links are purple, matching the green
  // (current) / purple (future) colouring of the quote number.
  const revNavGreen = `${revNavBase} text-green-600 dark:text-green-400`;
  const revNavPurple = `${revNavBase} text-purple-600 dark:text-purple-400`;
  const navClass = (label: string | null) =>
    label === 'Current' ? revNavGreen
    : label === 'Future' ? revNavPurple
    : revNavBlue;
  // The loaded revision is itself a future (not-yet-active) revision when it sits
  // above the current one. Colour the quote number to flag its state: green when
  // it IS the current/active revision, purple when it's a future one. But a
  // current revision whose end date has already passed reads as expired — grey it
  // out rather than green so the header signals the quote is no longer active.
  const isViewingFuture = viewedRev != null && currentRev != null && viewedRev > currentRev;
  const isExpiredCurrent = isCurrentRevision && isPastEndDate(headerData?.endDate);
  const quoteIdColor = isExpiredCurrent ? 'text-gray-400 dark:text-gray-500'
    : isCurrentRevision ? 'text-green-600 dark:text-green-400'
    : isViewingFuture ? 'text-purple-600 dark:text-purple-400'
    : undefined;
  const quoteIdLabel = createMode
    ? 'New Quote'
    : detail?.quoteNumber != null && detail.revisionNumber != null
      ? `${detail.quoteNumber}-${detail.revisionNumber}`
      : (lane?.quoteId ?? '');
  const drawerTitle = (
    <span className="inline-flex items-center flex-wrap gap-2">
      {detail?.quoteType && (
        <span className="px-2.5 py-[3px] text-[0.65625rem] font-bold uppercase tracking-wider rounded bg-indigo-50 text-indigo-800 border border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-700">
          {headerData?.quoteType ?? detail.quoteType} QUOTE
        </span>
      )}
      <MigrationVerifyBadge
        tracked={!!detail?.v2MigrationTracked}
        v2QuoteNumber={detail?.v2QuoteNumber}
        v2Lane={detail?.v2Lane}
        verified={migVerified}
        verifiedBy={migVerifiedBy}
        verifiedDate={migVerifiedDate}
        canVerify={!!detail?.v2MigrationTracked && quoteNumber != null}
        busy={migBusy}
        onToggle={handleToggleMigrationVerified}
      />
      {secured && (
        <span
          title="Secured — fields are locked. Only End Date can be edited."
          className="inline-flex items-center gap-1 px-2 py-[3px] text-[0.65625rem] font-bold uppercase tracking-wider rounded bg-amber-50 text-amber-800 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700"
        >
          <LockClosedIcon className="h-3 w-3" />
          Locked
        </span>
      )}
      {prevRev != null && backwardLabel && (
        <button type="button" onClick={() => goToRevision(prevRev)} className={navClass(backwardLabel)} title={`Go to revision ${prevRev}`}>
          <ChevronLeftIcon className="h-3.5 w-3.5" />
          {backwardLabel}
        </button>
      )}
      <span className={quoteIdColor}>{quoteIdLabel}</span>
      {nextRev != null && forwardLabel && (
        <button type="button" onClick={() => goToRevision(nextRev)} className={navClass(forwardLabel)} title={`Go to revision ${nextRev}`}>
          {forwardLabel}
          <ChevronRightIcon className="h-3.5 w-3.5" />
        </button>
      )}
    </span>
  );
  // Tab badges. Notes come free -- the payload already carries the rows. Documents
  // are keyed by revisionrecno in a separate service, so the backend sends a count
  // (docCount) rather than the rows. Falsy counts render no badge, matching the Pro
  // drawer, so a quote with nothing attached shows a clean strip.
  const subTabDefs = React.useMemo<ReadonlyArray<TabDef>>(() => {
    const noteCount = detail?.notes?.length ?? 0;
    const docCount = detail?.docCount ?? 0;
    const base = warehouseScoped
      ? [SUB_TABS[0], ...WHSE_SUB_TABS, ...SUB_TABS.slice(1)]
      : SUB_TABS;
    return base.map((t) => {
      if (t.id === 'notes') return noteCount ? { ...t, badge: noteCount } : t;
      if (t.id === 'docs') return docCount ? { ...t, badge: docCount } : t;
      return t;
    });
  }, [detail?.notes?.length, detail?.docCount, warehouseScoped]);

  // Follow Up bookmark, anchored to the right of the tab strip. This drawer is
  // the live quote detail surface (the standalone QuoteDetailPage is retired)
  // and it is what the Follow Up panel reopens. quoteFollowUp returns null until
  // BOTH number and revision exist, which self-guards create mode.
  const followUpBookmark = React.useMemo(() => {
    const bookmark = quoteFollowUp(detail?.quoteNumber, detail?.revisionNumber, detail?.customer);
    return bookmark ? (
      <FollowUpBookmark type={bookmark.type} itemId={bookmark.itemId} label={bookmark.label} />
    ) : null;
  }, [detail?.quoteNumber, detail?.revisionNumber, detail?.customer]);

  // Revision History link, hoisted out of the Quote Details card into the drawer
  // header (right side, next to the close X). Header-size font to match the title.
  const headerActions = detail?.quoteNumber != null ? (
    <button
      type="button"
      onClick={() => setHistoryOpen(true)}
      className="text-base font-medium text-blue-600 dark:text-blue-400 hover:underline"
    >
      Revision History
    </button>
  ) : undefined;

  // NO WAREHOUSE REDIRECT. A loaded WAREHOUSE quote used to close this drawer and
  // navigate to /order-management/quotes/warehouse/{q}-{r}, because the drawer could only
  // render DirectTab. That was wrong in both the places it fired:
  //
  //   - From the PRO Invoicing tab, clicking the quote number threw the user off the
  //     order entirely, taking any staged invoice line-item edits with it, and landed
  //     them on a route gated behind the /order-management/quotes permission — so an
  //     invoicing user without it got "Access Denied" instead of a quote.
  //   - Anywhere, it sent people to the standalone QuoteDetailPage, which this drawer
  //     had already replaced as the live quote surface (see the Follow Up note below).
  //
  // The three warehouse tabs are pure presentational components whose CRUD callbacks are
  // all optional, so rendering them here WITHOUT those callbacks gives a read-only view
  // for free — no duplicated UI, and no editing surface the drawer has not wired up.

  return (
    <>
    <Drawer
      isOpen={open}
      onClose={onClose}
      onBeforeClose={requestClose}
      title={drawerTitle}
      headerActions={headerActions}
      width={`${drawerWidth}px`}
      noBackdrop
      className="qled-inter"
      headerClassName="px-[18px] py-2"
      contentClassName="relative flex-1 overflow-y-auto"
    >
      {/* Resize handle — pinned to the panel's inner (left) edge. Rendered as a
          `fixed` strip tracking the panel edge (right: drawerWidth) instead of an
          absolute child, so it stays put while the drawer body scrolls. z-[101]
          keeps it grabbable above the drawer content (drawer panel is z-[100]).
          pointer-events-auto re-enables clicks (the drawer's outer wrapper is
          pointer-events-none). Drag to resize · double-click to snap to nav. */}
      {open && (
        <div
          data-testid="quote-lane-editor-resize-handle"
          className="fixed top-0 z-[101] h-full w-1 cursor-ew-resize bg-slate-200 transition-colors hover:bg-blue-400 active:bg-blue-500 pointer-events-auto dark:bg-gray-700 dark:hover:bg-blue-500 max-sm:hidden"
          style={{ right: `${drawerWidth - 4}px` }}
          onMouseDown={onHandleMouseDown}
          onDoubleClick={onHandleDoubleClick}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize panel"
          title="Drag to resize · double-click to snap to nav"
        />
      )}

      {/* Banners */}
      {/* EXPIRED — the loaded revision is the quote's current/active one and its
          End Date has already passed, so nothing is in effect for this quote.
          A solid red bar across the top so it can't be missed (the grey quote
          number alone was too subtle). Same predicate as the grey number; a
          superseded (older) revision is NOT expired — a newer revision took
          over — so it gets no banner. Not dismissible: the state is the fact. */}
      {!loading && detail && isExpiredCurrent && (
        <div
          role="alert"
          aria-label="Quote expired"
          className="bg-red-600 border-b border-red-800 px-[18px] py-2 flex items-center gap-2.5 text-white dark:bg-red-700 dark:border-red-900"
        >
          <span className="text-[0.6875rem] font-bold uppercase tracking-widest px-2 py-[2px] rounded border border-white/60 flex-shrink-0">
            Expired
          </span>
          <span className="text-[0.8125rem] font-semibold">
            This quote expired on {headerData?.endDate}. It is no longer in effect and is locked
            {isLatestRevision ? ' — use New Revision to reissue it.' : '.'}
          </span>
        </div>
      )}
      {actionMsg && (
        <div className="bg-amber-50 border-b border-amber-300 px-[18px] py-2.5 flex items-start justify-between gap-3 dark:bg-amber-900/30 dark:border-amber-700">
          <div className="text-[0.78125rem] text-amber-900 dark:text-amber-200 flex-1">{actionMsg}</div>
          <button type="button" onClick={() => setActionMsg(null)} aria-label="Dismiss" className="text-amber-600 hover:text-amber-900 dark:text-amber-400 dark:hover:text-amber-200 text-[0.8125rem] font-bold leading-none flex-shrink-0">×</button>
        </div>
      )}
      {conflict && (
        <div role="alert" className="bg-red-50 border-b border-red-300 px-[18px] py-2.5 flex items-start justify-between gap-3 dark:bg-red-900/30 dark:border-red-700">
          <div className="text-[0.78125rem] text-red-900 dark:text-red-300 flex-1">
            This quote was changed by someone else since you opened it. Reload to get the latest
            (your unsaved edits will be lost).
          </div>
          <button type="button" onClick={() => { void handleConflictReload(); }} className="px-3 py-1 text-[0.75rem] font-semibold text-white bg-red-600 border border-red-700 rounded-md cursor-pointer hover:bg-red-700 flex-shrink-0">
            Reload
          </button>
        </div>
      )}
      {saveErr && (
        <div role="alert" className="bg-red-50 border-b border-red-300 px-[18px] py-2.5 flex items-start justify-between gap-3 dark:bg-red-900/30 dark:border-red-700">
          <div className="text-[0.78125rem] text-red-900 dark:text-red-300 flex-1">{saveErr}</div>
          <button type="button" onClick={() => setSaveErr(null)} aria-label="Dismiss" className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-200 text-[0.8125rem] font-bold leading-none flex-shrink-0">×</button>
        </div>
      )}
      {saveMsg && (
        <div role="status" className="bg-green-50 border-b border-green-300 px-[18px] py-2.5 flex items-start justify-between gap-3 dark:bg-green-900/30 dark:border-green-700">
          <div className="text-[0.78125rem] text-green-900 dark:text-green-300 flex-1">{saveMsg}</div>
          <button type="button" onClick={() => setSaveMsg(null)} aria-label="Dismiss" className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-200 text-[0.8125rem] font-bold leading-none flex-shrink-0">×</button>
        </div>
      )}

      {/* Load states */}
      {loading && <LoadingPanel />}
      {!loading && loadError && (
        <div role="alert" className="m-[18px] flex items-center justify-between gap-3 rounded-md border border-red-300 bg-red-50 px-3 py-2.5 dark:border-red-800 dark:bg-red-900/30">
          <span className="text-[0.78125rem] text-red-800 dark:text-red-300">{loadError}</span>
          {quoteNumber != null && activeRevision != null && (
            <button
              type="button"
              onClick={() => { void load(activeRevision); }}
              className="flex-shrink-0 px-3 py-1 text-[0.75rem] font-semibold text-white bg-red-600 border border-red-700 rounded-md cursor-pointer hover:bg-red-700"
            >
              Retry
            </button>
          )}
        </div>
      )}

      {!loading && !loadError && detail && headerData && (
        // min-h-full makes this column fill the scroll area even when the
        // content is short (e.g. sections collapsed); the flex-1 sub-tab body
        // below then pushes the action bar to the bottom of the viewport. The
        // bar also stays `sticky bottom-0` so it remains pinned while tall
        // content scrolls behind it.
        <QuoteCardScaleProvider>
        <div className="flex flex-col min-h-full">

          {/* Tab strip, directly under the drawer header (Pro drawer layout).
              Everything below lives INSIDE the selected tab -- the account and
              Quote Details cards used to render outside the tabs entirely.
              The Follow Up bookmark rides the strip's right slot, anchored far
              right so a longer tab list scrolls underneath it. */}
          <Tabs
            tabs={subTabDefs}
            activeId={subTab}
            onChange={(id) => setSubTab(id as SubTab)}
            rightSlot={followUpBookmark}
          />

          {subTab === 'detail' && (<>
          {createMode && <div className="px-[18px] pt-2">
            <Row><Label width={120}>Quote Type</Label>
              <SelectInput ariaLabel="Quote Type" value={draft?.quoteType ?? 'DIRECT'} options={['DIRECT', 'WAREHOUSE']}
                onChange={(value) => { setDraft((d) => d ? { ...d, quoteType: value as 'DIRECT' | 'WAREHOUSE' } : d); setSaveErr(null); setDupeCandidates(null); setChargeCatalog([]); }} />
            </Row>
          </div>}
          {/* Account header — Bill To / Customer / Revenue & Statistics, shared
              with the grouped detail page (read-only). Padded with the same
              px-[18px] gutter as the QuoteDetails / Fuel & Revenue sections so
              the cards line up; its responsive grid reflows by drawer width. */}
          <div className="px-[18px] pt-2 pb-1">
            {/* The Bill To / Customer / Revenue & Statistics / Internal Comments
                cards collapse together as one "Account Details" section (the
                individual cards no longer carry their own chevrons — cardsCollapsible={false}). */}
            <Card
              compact
              title="Account Details"
              collapsed={sectionCollapsed.accountDetails}
              onCollapsedChange={(c) => setSection('accountDetails', c)}
            >
              <CustomerInfoTab
                data={headerData}
                variant="groupHeader"
                showIntelligence
                cardsCollapsible={false}
                /* Internal Comments are editable on ANY loaded quote — including
                   secured/ended (like SF Opp / CSR; the backend exempts
                   masterquote.mastercomments from those locks). */
                onCommentsChange={editable ? (v) => onRevisionFieldChange('internalComments', v) : undefined}
                /* SF Opp is editable on ANY loaded quote — including secured/ended
                   (like End Date); the backend exempts it from those locks. The
                   rest of the account block stays read-only in view mode. */
                onSfOppChange={editable ? (v) => onRevisionFieldChange('sfOpp', v) : undefined}
                /* CSR is likewise always editable (the assigned CSR can change at
                   any time; the backend exempts csr from the secured/ended locks).
                   Rendered as a pick-from-list typeahead over the CSR roster. */
                onCsrChange={editable ? (v) => onRevisionFieldChange('csr', v) : undefined}
                csrOptions={csrOptions}
                /* Create mode AND an unsecured original (revision 1 — e.g. a duped
                   quote) turn the account block editable + add the Bill To /
                   Customer typeaheads. A later revision / secured quote passes none
                   of these (undefined), so it stays read-only exactly as before.
                   In the groupHeader variant onHeaderChange only exposes account
                   fields (address / contact / other / contract #) — the revenue &
                   move fields live elsewhere — so this can't leak non-account edits. */
                onHeaderChange={accountEditable ? onRevisionFieldChange : undefined}
                onTypeBillTo={accountEditable ? handleTypeBillTo : undefined}
                onPickBillTo={accountEditable ? handlePickBillTo : undefined}
                onTypeCustomer={customerEditable ? handleTypeCustomer : undefined}
                onPickCustomer={customerEditable ? handlePickCustomer : undefined}
                onAddNewBillTo={accountEditable ? handleAddNewBillTo : undefined}
                onAddNewCustomer={customerEditable ? handleAddNewCustomer : undefined}
              />
            </Card>
          </div>

          {/* Quote Details header — editable revision fields + Prev/Next. The
              Revision History link is hoisted to the drawer header (below). */}
          <QuoteDetailHeader
            data={headerData}
            onRevisionChange={editable ? onRevisionFieldChange : undefined}
            showRevisionHistory={false}
            secured={secured}
            collapsed={sectionCollapsed.quoteDetails}
            onCollapsedChange={(c) => setSection('quoteDetails', c)}
            scaleKey="quoteDetails"
          />
          </>)}

          {warehouseScoped && subTab === 'detail' && <div className="px-[18px]">
            <Card title="Fuel & Rate Settings">
              <Row><Label width={120}>Fuel Calculation</Label><SelectInput ariaLabel="Fuel Calculation" value={headerData.fuelCalc} options={FUEL_CALC_TYPES} onChange={editable ? (v) => onRevisionFieldChange('fuelCalc', v) : undefined} disabled={!editable} /></Row>
              <Row><Label width={120}>Rate Applies</Label><SelectInput ariaLabel="Rate Applies" value={headerData.rateApplies} options={RATE_APPLIES} onChange={editable ? (v) => onRevisionFieldChange('rateApplies', v) : undefined} disabled={!editable} /></Row>
            </Card>
          </div>}

          {/* Sub-tab body — flex-1 so it absorbs any extra height and keeps the
              action bar at the bottom of the viewport when content is short. */}
          <div className="px-[18px] py-2.5 flex flex-col gap-2.5 flex-1">
            {subTab === 'detail' && (
              warehouseScoped ? (
                /* Dray rates live under 'Quote' alongside the header + customer cards,
                   the same slot DirectTab occupies for a direct quote. Editable with
                   the same gates as DirectTab: fields lock on a secured quote, charges
                   stay add-only until the end date passes. */
                warehouseData ? (
                  <DrayTab
                    createMode={warehouseCreate}
                    data={warehouseWithTotals(warehouseData, draftCharges, handlingItems)}
                    tariffRates={draftCharges.filter((r) => r.scope === 'transloadquote')}
                    onWarehouseChange={fieldsEditable && warehouseData ? onWarehouseChange : undefined}
                    chargesEditable={chargesInteractive}
                    chargeCatalog={chargeCatalog}
                    onAddCharge={chargesInteractive ? handleAddCharge : undefined}
                    onChangeCharge={chargesInteractive ? handleChangeCharge : undefined}
                    onDeleteCharge={chargesInteractive ? handleDeleteCharge : undefined}
                    rateZoneOptions={rateZoneOptions.map((o) => o.value)}
                    rateStandards={rateStandards}
                  />
                ) : (
                  <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-6 py-8 text-center text-slate-500 dark:text-slate-400 text-[0.78125rem]">
                    No warehouse data on this revision.
                  </div>
                )
              ) : detail.direct ? (
                <DirectTab
                  data={editable && draftDirect ? draftDirect : detail.direct}
                  tariffRates={draftCharges.filter((r) => r.scope === 'directquote')}
                  onDirectChange={fieldsEditable && draftDirect ? onDirectChange : undefined}
                  /* Lane Comments stay editable even when the quote is secured/
                     ended (the backend exempts directquote.comments). */
                  onCommentsChange={editable && draftDirect ? (v) => onDirectChange('comments', v) : undefined}
                  onDirectPickLocation={fieldsEditable && draftDirect ? onDirectPickLocation : undefined}
                  chargesEditable={chargesInteractive}
                  chargesAddOnly={chargesAddOnly}
                  chargeCatalog={chargeCatalog}
                  onAddCharge={chargesInteractive ? handleAddCharge : undefined}
                  onChangeCharge={chargesInteractive ? handleChangeCharge : undefined}
                  onDeleteCharge={chargesInteractive ? handleDeleteCharge : undefined}
                  stopoffs={draftStopoffs}
                  onAddStopoff={fieldsEditable ? handleAddStopoff : undefined}
                  onUpdateStopoff={fieldsEditable ? handleUpdateStopoff : undefined}
                  onDeleteStopoff={fieldsEditable ? handleDeleteStopoff : undefined}
                  quoteSaved={(detail.quoteNumber ?? 0) > 0}
                  rateZoneOptions={rateZoneOptions}
                  billTo={headerData.billToNumber ?? undefined}
                  onAssignTariff={tariffAssignable ? handleAssignTariff : undefined}
                  deletedTariffNumber={tariffDeleted ? Number(detail.direct?.fscTariff) : null}
                  moveFieldsLocked={(detail.revisionNumber ?? 0) > 1}
                  onMoveTypeChange={moveTypeEditable && draftDirect ? (v) => onDirectChange('moveType', v) : undefined}
                  fuelCalc={headerData.fuelCalc}
                  rateApplies={headerData.rateApplies}
                  /* Fuel Calculation / Rate Applies stay editable on ANY loaded
                     quote — including secured/ended — as in V1 (the backend
                     exempts masterquote.fuelcalctype + rateapplies). */
                  onFuelRateChange={editable ? onRevisionFieldChange : undefined}
                />
              ) : (
                <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-6 py-8 text-center text-slate-500 dark:text-slate-400 text-[0.78125rem]">
                  No direct data on this revision.
                </div>
              )
            )}
            {subTab === 'whse' && (
              warehouseData ? (
                <WarehouseTab
                  createMode={warehouseCreate}
                  data={warehouseWithTotals(warehouseData, draftCharges, handlingItems)}
                  onWarehouseChange={fieldsEditable && warehouseData ? onWarehouseChange : undefined}
                  rateStandards={rateStandards}
                  handlingItems={handlingItems}
                  handlingCatalog={handlingCatalog}
                  onAddHandling={warehouseCreate ? stageHandling : fieldsEditable ? handleAddHandling : undefined}
                  onChangeHandling={warehouseCreate ? changeStagedHandling : fieldsEditable ? handleChangeHandling : undefined}
                  onDeleteHandling={warehouseCreate ? (id) => setDraftHandling((rows) => rows.filter((r) => r.id !== id)) : fieldsEditable ? handleDeleteHandling : undefined}
                  onAddProductRate={fieldsEditable && !createMode ? handleAddProductRate : undefined}
                  onUpdateProductRate={fieldsEditable && !createMode ? handleUpdateProductRate : undefined}
                  onDeleteProductRate={fieldsEditable && !createMode ? handleDeleteProductRate : undefined}
                  onProductCodeLookup={fieldsEditable ? (code) => quoteService.productCodeLookup(code) : undefined}
                  onWhseCustomerLookup={fieldsEditable ? (qq) => quoteService.lookupWhseCustomer(qq) : undefined}
                />
              ) : (
                <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-6 py-8 text-center text-slate-500 dark:text-slate-400 text-[0.78125rem]">
                  No warehouse data on this revision.
                </div>
              )
            )}
            {subTab === 'deliv' && (warehouseCreate ? (
              <Card title="Delivery Lanes">
                <WarehouseLaneFields lanes={draftLanes} ratezone={draftTransload.ratezone ?? ''}
                  addLane={() => setDraftLanes((rows) => [...rows, { delwarehouse: draftTransload.warehouse ?? '' }])}
                  updateLane={(index, patch) => setDraftLanes((rows) => rows.map((r, i) => i === index ? { ...r, ...patch } : r))}
                  removeLane={(index) => setDraftLanes((rows) => rows.filter((_, i) => i !== index))} />
              </Card>
            ) : (
              <DeliveryTab
                rateZone={warehouseData?.rateZone ?? ''}
                lanes={detail.deliveryLanes ?? []}
                tariffRates={(detail.tariffRates ?? []).filter((r) => r.scope === 'deliveryquote')}
                lanesEditable={fieldsEditable}
                onAddLane={editable && !endedPast ? handleAddLane : undefined}
                onSaveLane={fieldsEditable ? handleSaveLane : undefined}
                onDeleteLane={fieldsEditable ? handleDeleteLane : undefined}
                onAddStopoff={fieldsEditable ? handleAddLaneStopoff : undefined}
                onUpdateStopoff={fieldsEditable ? handleUpdateLaneStopoff : undefined}
                onDeleteStopoff={fieldsEditable ? handleDeleteLaneStopoff : undefined}
                /* Secured (not ended): lanes lock but NEW charges may still be
                   added — add-only, mirroring DirectTab's secured charges. */
                chargesEditable={chargesInteractive}
                chargesAddOnly={chargesAddOnly}
                chargeCatalog={deliveryChargeCatalog}
                onAddLaneCharge={chargesInteractive ? handleAddLaneCharge : undefined}
                onChangeCharge={chargesInteractive ? handleChangeLaneCharge : undefined}
                onDeleteCharge={chargesInteractive ? handleDeleteLaneCharge : undefined}
                rateStandards={rateStandards}
              />
            ))}
            {subTab === 'docs' && (
              <DocumentsTab quoteNumber={detail.quoteNumber} revisionNumber={detail.revisionNumber} />
            )}
            {subTab === 'notes' && (
              <NotesTab
                notes={detail.notes ?? []}
                quoteNumber={detail.quoteNumber}
                revisionNumber={detail.revisionNumber}
                canAdd={editable}
                onAdded={() => { if (detail.revisionNumber != null) void reload(detail.revisionNumber); }}
              />
            )}
            {subTab === 'hist' && <HistoryTab quoteNumber={detail.quoteNumber} />}
          </div>

          {/* Action bar — pinned to the bottom of the drawer's scroll area so the
              Save / actions stay reachable while the (long) content scrolls behind
              it. Opaque bg (matches the panel) so content doesn't show through. */}
          <div className="sticky bottom-0 z-10 flex items-center justify-end gap-2 px-[18px] py-3 flex-wrap border-t border-slate-200 bg-theme-bg-secondary dark:border-slate-700">
            {createMode ? (
              /* Create mode: the revision/dupe/send/PDF actions need a saved quote,
                 so they're hidden — just Create Quote (POST → stay in the drawer). */
              <SaveBtn
                onClick={() => { void handleCreate(); }}
                disabled={saving}
                label={saving ? 'Creating…' : 'Create Quote'}
              />
            ) : (
              <>
                {isDirty && <span className="mr-auto text-[0.6875rem] font-semibold text-amber-700 dark:text-amber-400">Unsaved changes</span>}
                {isLatestRevision && <GhostBtn onClick={handleMakeRevision} disabled={!editable}>New Revision</GhostBtn>}
                <GhostBtn onClick={handleDuplicate} disabled={!editable}>Duplicate</GhostBtn>
                <GhostBtn onClick={handleSend} disabled={!editable}>Send…</GhostBtn>
                <GhostBtn onClick={() => { void handleViewPdf(); }} disabled={!editable}>View PDF</GhostBtn>
                {/* Same PDF, spooled to a CUPS laser printer (print-send gated). */}
                {detail?.quoteNumber != null && detail.revisionNumber != null && (
                  <PrintButton
                    endpoint={`/quotes/${detail.quoteNumber}/${detail.revisionNumber}/print`}
                    title="Print Quote"
                    disabled={!editable}
                    className="px-3.5 py-1.5 text-[0.78125rem] font-medium text-slate-700 bg-white border border-slate-300 rounded-md cursor-pointer hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600 inline-flex items-center gap-1.5"
                  />
                )}
                {editable && (
                  <>
                    <GhostBtn onClick={revert} disabled={!isDirty || saving}>Revert</GhostBtn>
                    <SaveBtn onClick={() => { void handleSave(); }} disabled={!isDirty || saving} label={saving ? 'Saving…' : 'Save'} />
                  </>
                )}
              </>
            )}
          </div>
        </div>
        </QuoteCardScaleProvider>
      )}
    </Drawer>

    {/* Action dialogs render after <Drawer>. NewRevisionDialog self-portals to
        <body> (z-[120]), so it escapes the drawer panel's CSS transform and
        centers on the full viewport regardless of where it's mounted.
        SendQuoteDialog does NOT portal, so it relies on rendering as a SIBLING
        of <Drawer> here: the panel's transform would otherwise become the
        containing block for its `position: fixed` and confine it to the panel.
        As a sibling its fixed positioning escapes the transform and, in DOM
        order, paints above the drawer (both z-[100]). */}
    {revisionDialogOpen && detail?.quoteNumber != null && (
      <NewRevisionDialog
        open={revisionDialogOpen}
        quoteNumber={detail.quoteNumber}
        quoteType={detail.quoteType}
        currentRevision={detail.revisionNumber ?? 0}
        busy={creatingRevision}
        onSubmit={(p) => { void submitNewRevision(p); }}
        onCancel={() => setRevisionDialogOpen(false)}
      />
    )}
    {dupeDialogOpen && detail?.quoteNumber != null && (
      <NewRevisionDialog
        open={dupeDialogOpen}
        mode="dupe"
        quoteNumber={detail.quoteNumber}
        quoteType={detail.quoteType}
        currentRevision={detail.revisionNumber ?? 0}
        busy={duping}
        onSubmit={(p) => { void submitDupe(p); }}
        onCancel={() => setDupeDialogOpen(false)}
      />
    )}
    {sendDialogOpen && detail?.quoteNumber != null && detail.revisionNumber != null && (
      <SendQuoteDialog
        open={sendDialogOpen}
        quoteNumber={detail.quoteNumber}
        revisionNumber={detail.revisionNumber}
        onClose={() => setSendDialogOpen(false)}
      />
    )}
    {historyOpen && detail?.quoteNumber != null && (
      <RevisionHistoryModal
        open={historyOpen}
        quoteNumber={detail.quoteNumber}
        currentRevision={detail.revisionNumber ?? undefined}
        onClose={() => setHistoryOpen(false)}
        onSelectRevision={(rev) => {
          setHistoryOpen(false);
          goToRevision(rev);
        }}
      />
    )}

    <WarningConfirmModal
      open={showCloseConfirm}
      title="Discard changes?"
      message="You have unsaved changes. Close the quote without saving?"
      confirmLabel="Discard"
      cancelLabel="Keep editing"
      onConfirm={() => { setShowCloseConfirm(false); onClose(); }}
      onCancel={() => setShowCloseConfirm(false)}
    />

    <ConfirmSecuredChangesModal
      open={confirmAddOpen}
      charges={draftCharges.filter((r) => r.id < 0)}
      tariff={tariffAssignedThisSession && draftDirect
        ? { tariffNumber: Number(draftDirect.fscTariff), rate: draftDirect.fscRate }
        : null}
      busy={saving}
      onConfirm={handleConfirmAddCharges}
      onCancel={() => setConfirmAddOpen(false)}
    />

    {/* New Revision — unsaved-changes guard. */}
    <WarningConfirmModal
      open={showRevisionDirtyConfirm}
      title="Unsaved changes"
      message="You have unsaved changes. Cancel to go back and save them, or discard them and create a new revision from the last saved version."
      confirmLabel="Discard & Continue"
      cancelLabel="Cancel"
      onConfirm={() => { setShowRevisionDirtyConfirm(false); revert(); proceedMakeRevision(); }}
      onCancel={() => setShowRevisionDirtyConfirm(false)}
    />

    {/* New Revision — End Date required on the current revision. */}
    <AlertModal
      open={showEndDateAlert}
      title="End Date required"
      message="This revision needs an End Date before you can create a new revision. Set the End Date on this quote, save, then try again."
      onClose={() => setShowEndDateAlert(false)}
    />

    {/* Create dupe-check (V1 checkfordupes): an active quote for this bill-to +
        lane already exists. Open one in place, or create anyway. Portaled to
        <body> so it layers ABOVE the drawer (which is itself a body portal at
        z-[100]); an in-tree sibling would render behind it. */}
    {dupeCandidates && createPortal(
      <div
        className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/45 backdrop-blur-sm p-4"
        onMouseDown={(e) => { if (e.target === e.currentTarget) setDupeCandidates(null); }}
      >
        <div ref={dupePanelRef} style={dupePanelStyle} role="dialog" aria-label="Duplicate quotes found" className="bg-white dark:bg-slate-900 rounded-lg shadow-xl w-full max-w-lg border border-slate-200 dark:border-slate-700">
          <ModalHeader
            title="Duplicate Quotes Found"
            onClose={() => setDupeCandidates(null)}
            dragHandleProps={dupeDragHandleProps}
          />
          <div className="px-4 pt-3">
            <p className="m-0 text-[0.71875rem] text-slate-500 dark:text-slate-400">An active quote for this bill-to &amp; lane already exists. Open one in a new tab to review, or create anyway.</p>
          </div>
          <div className="px-4 py-3 max-h-[50vh] overflow-y-auto flex flex-col gap-1">
            {dupeCandidates.map((c) => (
              <button
                key={`${c.quoteNumber}-${c.revisionNumber}`}
                type="button"
                onClick={() => window.open(`/order-management/quotes/${c.quoteNumber}-${c.revisionNumber}`, '_blank', 'noopener')}
                title="Open in a new tab to review"
                className="w-full text-left px-2.5 py-1.5 rounded border border-slate-200 dark:border-slate-700 hover:bg-blue-50 dark:hover:bg-slate-800 flex items-center justify-between gap-2"
              >
                <span className="min-w-0">
                  <span className="block text-[0.78125rem] font-semibold text-slate-800 dark:text-slate-100 truncate">
                    {c.quoteNumber}-{c.revisionNumber}
                    <span className="ml-2 font-normal text-slate-500 dark:text-slate-400">{c.billtoname}</span>
                  </span>
                  {c.lane && <span className="block text-[0.6875rem] text-slate-500 dark:text-slate-400 truncate">{c.lane}</span>}
                </span>
                <span className="flex-shrink-0 inline-flex items-center gap-1 text-[0.6875rem] font-semibold text-blue-600 dark:text-blue-400">
                  Open <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                </span>
              </button>
            ))}
          </div>
          <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-2">
            <button type="button" onClick={() => setDupeCandidates(null)} className="px-3 py-1.5 text-[0.75rem] text-slate-600 hover:text-slate-800 dark:text-slate-300 dark:hover:text-slate-100">Cancel</button>
            <button type="button" onClick={() => { setDupeCandidates(null); void handleCreate(true); }} className="px-3 py-1.5 text-[0.75rem] font-semibold text-white bg-blue-600 rounded hover:bg-blue-700">Create Anyway</button>
          </div>
        </div>
      </div>,
      document.body,
    )}

    {/* "Add As New Customer" — full V1-parity form; on save it links the new
        customer onto the draft like a pick. */}
    {newCustomerOpen && (
      <NewCustomerModal
        initialName={newCustomerName}
        onClose={() => setNewCustomerOpen(false)}
        onCreated={(res) => {
          setNewCustomerOpen(false);
          handlePickCustomer({
            customercode: res.customercode, custname: res.custname,
            address1: '', address2: '', city: '', state: '', zip: '',
            marketcode: res.marketcode, market: '', keyAccount: '', salesrep: '',
          });
        }}
      />
    )}

    {/* "Add As New Bill To" — V1 defers (no cms.billtos row): the quote carries
        billto=0 with the typed name/address; Accounting reconciles it later. */}
    {newBillToOpen && (
      <NewBillToModal
        initialName={newBillToName.trim()}
        onClose={() => setNewBillToOpen(false)}
        onCreated={(f) => {
          setNewBillToOpen(false);
          handlePickBillTo({
            billto: 0, billtoname: f.name,
            address1: f.address1, address2: '',
            city: f.city, state: f.state, zip: f.zip,
            billtofax: f.fax, salescsr: '', keyAccount: '',
            creditTerms: '', creditHold: '', mas90code: '', arBalance: '', defaultFuelCalc: '',
          });
          // contact/phone are V1 new-bill-to fields not on the bill-to lookup row.
          setDraft((d) => (d ? { ...d, contact: f.contact, phone: f.phone } : d));
        }}
      />
    )}
    </>
  );
};

const LoadingPanel: React.FC = () => (
  <div role="status" aria-label="Loading quote" className="animate-pulse flex flex-col gap-3 p-[18px]">
    <div className="h-6 w-48 rounded bg-slate-200 dark:bg-slate-700" />
    <div className="h-32 w-full rounded bg-slate-200 dark:bg-slate-700" />
    <div className="h-24 w-full rounded bg-slate-200 dark:bg-slate-700" />
  </div>
);

const GhostBtn: React.FC<{ children: React.ReactNode; onClick?: () => void; disabled?: boolean }> = ({ children, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="px-3.5 py-1.5 text-[0.78125rem] font-medium text-slate-700 bg-white border border-slate-300 rounded-md cursor-pointer hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600"
  >
    {children}
  </button>
);

const SaveBtn: React.FC<{ onClick?: () => void; disabled?: boolean; label?: string }> = ({ onClick, disabled, label }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="px-5 py-1.5 text-[0.78125rem] font-semibold text-white bg-blue-600 border border-blue-700 rounded-md cursor-pointer inline-flex items-center gap-1.5 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
  >
    {label ?? 'Save'}
  </button>
);

export default QuoteLaneEditorDrawer;
